import {useEffect, useMemo, useRef, useState} from 'react'
import axios from 'axios'
import {DatePicker} from '@material-ui/pickers'
import {format} from 'date-fns'
import {Routes, Route, useMatch, useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'

import {Modal} from '../../../../../_metronic/elements/Modal'
import {openAlert} from '../../../../../_metronic/elements/NotificationAlert'
import {useErrorHandler} from '../../../../../_metronic/helpers/ErrorHandler'
import {KTSVG} from '../../../../../_metronic/helpers'
import {formatHectares} from '../../../../shared/numberFormat'
import {useIsMobile} from '../../../../shared/responsive'
import {useSearchContext} from '../../Toolbar/SearchContext'
import {useAuth} from '../../../auth/core/Auth'
import {canAccessHistoricalHd} from '../../../auth/core/historicalHdAccess'
import {
  getLandDates,
  getLandHdAccess,
  searchLandHdAcquisitions,
} from '../../Fields/services/GisDataAdapter'
import * as ReportService from '../services/ReportService'
import {
  buildFieldLandDownloadSubject,
  buildLandDownloadName,
} from '../../Fields/ViewField/ViewLand/Layers/downloadArchive'
import {downloadTechnicalReportPdf} from './technicalReportPdf'
import {buildTechnicalReportVisualSections} from './technicalReportVisuals'
import {PLANET_HD_V1_ENABLED} from '../../../../config/featureFlags'
import {canUsePlanetHd} from '../../Fields/ViewField/ViewLand/Images/planetHdAccessModel'
import {
  isRemoteSensingDateVisualizable,
  normalizeRemoteSensingDateItems,
} from '../../Fields/ViewField/ViewLand/Images/remoteSensingDateModel'
import {buildPlanetHdAcquisitionQuery, isPlanetHdDateAllowed} from '../../Fields/ViewField/ViewLand/Images/planetHdAcquisitionQuery'
import {selectBestPlanetHdItemsByDate} from '../../Fields/ViewField/ViewLand/Images/planetHdCalendarModel'
import {
  REMOTE_SENSING_PRODUCTS,
  isPlanetHdProduct,
  normalizeRemoteSensingSource,
} from '../../Fields/ViewField/ViewLand/Images/remoteSensingModel'
import {
  buildImageLayerCacheOptions,
  requestRemoteSensingLayer,
} from '../../Fields/ViewField/ViewLand/Images/remoteSensingLayerRequest'
import {buildRemoteSensingCacheKey} from '../../Fields/ViewField/ViewLand/Images/remoteSensingCacheKey'
import {
  ALL_TECHNICAL_IMAGE_TYPES,
  TECHNICAL_REPORT_SOURCE_KEY,
  buildTechnicalReportSourceMeta,
  getTechnicalReportImageTypes,
  getTechnicalReportProduct,
  getTechnicalReportSourceLabel,
  getTechnicalReportSourceMeta,
  hasCompleteHdAcquisitionSelection,
  hasCompleteStoredHdAcquisitionSelection,
  withTechnicalReportSource,
} from './technicalReportSource'
import ReportDeleteConfirmation from '../ReportDeleteConfirmation'
import {
  buildTechnicalReportCacheKey,
  createTechnicalReportCacheState,
  getTechnicalReportTenantDomain,
  getVisibleTechnicalReports,
  readTechnicalReportCache,
  removeLegacyTechnicalReportCache,
  writeTechnicalReportCache,
} from './technicalReportCache'
import {
  applyImageTypeDatesToAllLands,
  areImageTypeDatesAppliedToAllLands,
  copyImageTypeDatesForLand,
} from './technicalReportImageSelection'
import '../NDVIReport/Report.scss'
import '../../Fields/ViewField/ViewLand/Images/DateSelector.style.scss'
import './TechnicalReport.scss'

const IMAGE_TYPES = ALL_TECHNICAL_IMAGE_TYPES
const ENHANCEABLE_IMAGE_TYPES = ['ndvi', 'evi']
const DEFAULT_CLOUDINESS_FILTER = 20
const MAX_TECHNICAL_REPORT_LANDS = 25
const TECHNICAL_REPORT_WIZARD_ORDER = [
  'identity',
  'scope',
  'dates',
  'content',
  'images',
  'observations',
  'preview',
]

const CONTENT_OPTIONS = [
  'overview_map',
  'visible_images',
  'ndvi_images',
  'evi_images',
  'ndre_images',
  'altimetry',
  'soil_map',
  'historical_ambient',
  'image_ambient',
]

const TECHNICAL_REPORT_PAGE_SIZE = 20

const getLandId = (land) => land.id || land.land_id

const getLandHa = (land) => Number(land.ha || land.sup_ha || 0)

const getFieldLandDisplayName = (land) =>
  buildFieldLandDownloadSubject({
    fieldName: land?.field?.name || land?.field_name,
    landName: land?.name,
  }) || land?.name || ''

const getTodayInputValue = () => format(new Date(), 'yyyy-MM-dd')

const formatInputDate = (value) => {
  if (!value) {
    return '-'
  }

  const [year, month, day] = value.split('-')
  return `${day}/${month}/${year}`
}

const getContentKeyForImageType = (type) => `${type}_images`

const sortDatesDescending = (dates = []) =>
  [...dates].sort((left, right) => new Date(right) - new Date(left))

const createInitialImageTypeSelections = (source = {}) =>
  IMAGE_TYPES.reduce((accumulator, type) => {
    accumulator[type] = Array.isArray(source[type])
      ? source[type].filter(Boolean).slice(0, 5)
      : []
    return accumulator
  }, {})

const createInitialImageEnhancements = (source = {}) =>
  ENHANCEABLE_IMAGE_TYPES.reduce((accumulator, type) => {
    accumulator[type] = Boolean(source[type])
    return accumulator
  }, {})

const normalizeStoredContent = (content = {}, {soilMapDefault = false} = {}) =>
  CONTENT_OPTIONS.reduce((accumulator, option) => {
    const defaultValue = option === 'soil_map'
      ? soilMapDefault
      : option === 'ndre_images'
        ? false
        : true
    accumulator[option] = content[option] !== undefined ? Boolean(content[option]) : defaultValue
    return accumulator
  }, {})

const normalizeStoredImageSelections = (imageSelections = {}) => {
  const sourceMeta = getTechnicalReportSourceMeta(imageSelections)
  const normalized = Object.entries(imageSelections).reduce((accumulator, [landId, selections]) => {
    if (!landId || landId === TECHNICAL_REPORT_SOURCE_KEY || IMAGE_TYPES.includes(landId)) {
      return accumulator
    }

    accumulator[landId] = createInitialImageTypeSelections(selections)
    return accumulator
  }, {})

  if (sourceMeta) {
    normalized[TECHNICAL_REPORT_SOURCE_KEY] = sourceMeta
  }

  return normalized
}

const getLandImageSelections = (imageSelections = {}, landId) =>
  createInitialImageTypeSelections(imageSelections[landId])

const normalizeStoredSelected = (selected = {}) =>
  Object.entries(selected).reduce((accumulator, [landId, value]) => {
    if (value) {
      accumulator[landId] = true
    }
    return accumulator
  }, {})

const createTechnicalReportId = () =>
  `technical-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const normalizeStoredTechnicalReport = (report) => {
  if (!report || !report.id) {
    return null
  }

  const status = report.status === 'in_progress'
    ? 'error'
    : report.status || 'completed'

  return {
    id: report.id,
    name: report.name || report.reportName || '',
    status,
    createdAt: report.createdAt || report.updatedAt || new Date().toISOString(),
    updatedAt: report.updatedAt || report.createdAt || new Date().toISOString(),
    dateRange: {
      from: report.dateRange?.from || '',
      to: report.dateRange?.to || getTodayInputValue(),
    },
    selected: normalizeStoredSelected(report.selected),
    content: normalizeStoredContent(report.content),
    imageSelections: normalizeStoredImageSelections(report.imageSelections),
    imageEnhancements: createInitialImageEnhancements(report.imageEnhancements),
    notes: {
      observations: report.notes?.observations || '',
    },
    artifacts: report.artifacts || {},
    landIds: report.landIds || report.land_ids || [],
    totalHa: report.totalHa ?? report.total_ha ?? null,
  }
}

const readStoredTechnicalReports = (storageKey) => readTechnicalReportCache(storageKey)
  .map(normalizeStoredTechnicalReport)
  .filter(Boolean)
  .sort((left, right) => new Date(right.updatedAt) - new Date(left.updatedAt))

const writeStoredTechnicalReports = (storageKey, reports) => {
  writeTechnicalReportCache(storageKey, reports)
}

const buildTechnicalReportRecord = (draft) => {
  const now = new Date().toISOString()

  return {
    id: draft.id || createTechnicalReportId(),
    name: draft.name || draft.reportName || '',
    status: draft.status || 'completed',
    createdAt: draft.createdAt || now,
    updatedAt: now,
    dateRange: {
      from: draft.dateRange?.from || '',
      to: draft.dateRange?.to || getTodayInputValue(),
    },
    selected: normalizeStoredSelected(draft.selected),
    content: normalizeStoredContent(draft.content),
    imageSelections: normalizeStoredImageSelections(draft.imageSelections),
    imageEnhancements: createInitialImageEnhancements(draft.imageEnhancements),
    notes: {
      observations: draft.notes?.observations || '',
    },
    artifacts: draft.artifacts || {},
    landIds: draft.landIds || [],
    totalHa: draft.totalHa ?? null,
  }
}

const buildTechnicalReportApiPayload = (draft, landIds) => ({
  id: draft.id,
  name: draft.name || draft.reportName || '',
  landIds,
  fromDate: draft.dateRange?.from || '',
  toDate: draft.dateRange?.to || getTodayInputValue(),
  content: normalizeStoredContent(draft.content),
  imageSelections: normalizeStoredImageSelections(draft.imageSelections),
  imageEnhancements: createInitialImageEnhancements(draft.imageEnhancements),
  notes: {
    observations: draft.notes?.observations || '',
  },
})

const FieldSelector = ({field, mobile, selected, toggleField, toggleLand}) => {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const lands = field.lands || []
  const fieldId = field.field_id || field.id
  const allSelected = lands.length > 0 && lands.every((land) => selected[getLandId(land)])

  if (!mobile) {
    return (
      <div className='accordion-item'>
        <h2
          className='accordion-header reports-desktop-field-group'
          id={`technical_report_scope_header_${fieldId}`}
          onClick={() => setOpen((current) => !current)}
        >
          <button
            className={`reports-desktop-field-group__button ${open ? '' : 'collapsed'}`}
            type='button'
            aria-expanded={open}
            aria-controls={`technical_report_scope_body_${fieldId}`}
          >
            <span>{field.name}</span>
          </button>
          <label
            className='form-check form-switch form-check-custom form-check-solid reports-desktop-field-group__select-all'
            onClick={(event) => event.stopPropagation()}
          >
            <input
              className='form-check-input'
              type='checkbox'
              aria-label={t('reports:technical.form.scope.select_field')}
              checked={allSelected}
              onChange={(event) => {
                event.stopPropagation()
                toggleField(field)
              }}
            />
          </label>
        </h2>
        <div
          id={`technical_report_scope_body_${fieldId}`}
          className={`accordion-collapse collapse ${open ? 'show' : ''}`}
          aria-labelledby={`technical_report_scope_header_${fieldId}`}
        >
          <div className='accordion-body p-0'>
            {lands.map((land) => {
              const landId = getLandId(land)

              return (
                <label className='d-flex align-items-center p-5 land-item cursor-pointer' key={landId}>
                  <span className='flex-grow-1'>
                    <span className='text-gray-800 text-hover-primary fw-bold fs-6'>{land.name}</span>
                  </span>
                  <input
                    type='checkbox'
                    checked={Boolean(selected[landId])}
                    onChange={() => toggleLand(land)}
                  />
                </label>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='reports-mobile-field-group'>
      <button
        type='button'
        className='reports-mobile-field-group__header'
        onClick={() => setOpen((current) => !current)}
      >
        <span className='reports-mobile-field-group__copy'>
          <span className='reports-mobile-field-group__title'>{field.name}</span>
          <span className='reports-mobile-field-group__meta'>{lands.length} lotes</span>
        </span>
        <span className='reports-mobile-field-group__actions'>
          <input
            type='checkbox'
            checked={allSelected}
            onChange={(event) => {
              event.stopPropagation()
              toggleField(field)
            }}
            onClick={(event) => event.stopPropagation()}
          />
          <KTSVG
            path={open ? '/media/icons/duotune/arrows/arr072.svg' : '/media/icons/duotune/arrows/arr071.svg'}
            className='svg-icon svg-icon-2'
          />
        </span>
      </button>
      {open && (
        <div className='reports-mobile-field-group__body'>
          {lands.map((land) => {
            const landId = getLandId(land)

            return (
              <label className='reports-mobile-land-item' key={landId}>
                <span className='reports-mobile-land-item__copy'>
                  <span className='reports-mobile-land-item__name'>{land.name}</span>
                </span>
                <input
                  type='checkbox'
                  checked={Boolean(selected[landId])}
                  onChange={() => toggleLand(land)}
                />
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

const MetricCard = ({label, value}) => (
  <div className='technical-report-metric'>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
)

const TechnicalReportImageThumbnail = ({
  acquisition,
  dateValue,
  enhanced = false,
  expandable = false,
  land,
  onExpand,
  position,
  product,
  thumbnailCache,
  type,
}) => {
  const {t} = useTranslation()
  const [imageUrl, setImageUrl] = useState(null)
  const [status, setStatus] = useState('idle')

  useEffect(() => {
    const landId = getLandId(land)
    if (!dateValue || !landId) {
      setImageUrl(null)
      setStatus('idle')
      return undefined
    }

    if (isPlanetHdProduct(product) && !acquisition?.acquisitionId) {
      setImageUrl(null)
      setStatus('error')
      return undefined
    }

    const source = normalizeRemoteSensingSource({product, layer: type})
    const canEnhance = !isPlanetHdProduct(product) && ENHANCEABLE_IMAGE_TYPES.includes(type)
    const shouldEnhance = canEnhance && enhanced
    const cacheOptions = buildImageLayerCacheOptions({
      acquisition,
      date: dateValue,
      enhanced: shouldEnhance,
      imageType: source.imageType,
      land,
    })
    const cacheKey = buildRemoteSensingCacheKey({
      ...cacheOptions,
      landId,
    })
    const cachedImageUrl = thumbnailCache?.get(cacheKey)

    if (cachedImageUrl) {
      setImageUrl(cachedImageUrl)
      setStatus('ready')
      return undefined
    }

    let objectUrl = null
    let cancelled = false
    const cancelSource = axios.CancelToken.source()

    setStatus('loading')
    setImageUrl(null)

    requestRemoteSensingLayer({
      acquisition,
      cancelTokenRequest: cancelSource,
      imageType: source.imageType,
      land: {...land, land_id: landId},
      layer: type,
      options: {
        date: dateValue,
        ...(shouldEnhance ? {enhanced: true} : {}),
      },
    })
      .then((res) => {
        if (cancelled || !res?.ok) {
          return
        }

        objectUrl = window.URL.createObjectURL(res.data)
        thumbnailCache?.set(cacheKey, objectUrl)
        setImageUrl(objectUrl)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error')
        }
      })

    return () => {
      cancelled = true
      cancelSource.cancel('technical-report-thumbnail-replaced')
      if (objectUrl && !thumbnailCache?.has(cacheKey)) {
        window.URL.revokeObjectURL(objectUrl)
      }
    }
  }, [acquisition, dateValue, enhanced, land, product, thumbnailCache, type])

  const canExpand = expandable && imageUrl && onExpand

  return (
    <div
      className={`technical-report-image-carousel__frame technical-report-image-carousel__frame--${position} is-${status} ${canExpand ? 'is-expandable' : ''}`.trim()}
      aria-label={canExpand ? t('reports:technical.form.images.expand') : undefined}
      onClick={canExpand ? onExpand : undefined}
      onKeyDown={canExpand ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onExpand()
        }
      } : undefined}
      role={canExpand ? 'button' : undefined}
      tabIndex={canExpand ? 0 : undefined}
    >
      {imageUrl ? (
        <>
          <img src={imageUrl} alt={`${type} ${formatInputDate(dateValue)}`} />
          {expandable && <span className='technical-report-image-carousel__expand' aria-hidden='true' />}
        </>
      ) : (
        <span>{status === 'loading' ? t('reports:technical.form.images.loading_dates') : t('reports:technical.form.images.thumbnail_unavailable')}</span>
      )}
    </div>
  )
}

const ImageDateSelector = ({
  availableDates = [],
  clearDates = [],
  cloudyDates = [],
  dateRange,
  dates,
  enhanced = false,
  itemsByDate = {},
  land,
  isLoadingDates,
  onAddDate,
  onRemoveDate,
  onSetDates,
  product,
  thumbnailCache,
  type,
}) => {
  const {t} = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)
  const [expandedDate, setExpandedDate] = useState(null)
  const [showOnlyClearImages, setShowOnlyClearImages] = useState(false)
  const previousCarouselDatesKey = useRef('')
  const clearDateSet = useMemo(() => new Set(clearDates), [clearDates])
  const cloudyDateSet = useMemo(() => new Set(cloudyDates), [cloudyDates])
  const datesInRange = useMemo(() => {
    return availableDates
      .filter((dateValue) => {
        if (dateRange.from && dateValue < dateRange.from) {
          return false
        }

        if (dateRange.to && dateValue > dateRange.to) {
          return false
        }

        return true
      })
      .sort((left, right) => new Date(left) - new Date(right))
  }, [availableDates, dateRange.from, dateRange.to])
  const carouselDates = useMemo(() => {
    if (!showOnlyClearImages) {
      return datesInRange
    }

    return datesInRange.filter((dateValue) => clearDateSet.has(dateValue))
  }, [clearDateSet, datesInRange, showOnlyClearImages])
  const carouselDatesKey = carouselDates.join('|')
  const hasDatesInRange = datesInRange.length > 0
  const hasAvailableDates = carouselDates.length > 0
  const safeActiveIndex = hasAvailableDates ? Math.min(activeIndex, carouselDates.length - 1) : 0
  const activeDate = carouselDates[safeActiveIndex] || null
  const previousDate = carouselDates[safeActiveIndex - 1] || null
  const nextDate = carouselDates[safeActiveIndex + 1] || null
  const selectedDateSet = useMemo(() => new Set(dates), [dates])
  const isActiveSelected = activeDate ? selectedDateSet.has(activeDate) : false
  const reachedLimit = dates.length >= 5
  const selectionDisabled = !activeDate || (!isActiveSelected && reachedLimit)
  const latestDates = useMemo(
    () => carouselDates.slice(-5).sort((left, right) => new Date(right) - new Date(left)),
    [carouselDates]
  )
  const latestDateSet = useMemo(() => new Set(latestDates), [latestDates])
  const hasLatestDates = latestDates.length > 0
  const latestDatesSelected = hasLatestDates && latestDates.every((dateValue) => selectedDateSet.has(dateValue))
  const activeDateIsClear = activeDate ? clearDateSet.has(activeDate) : false
  const activeDateIsCloudy = activeDate ? cloudyDateSet.has(activeDate) : false
  const activeDateStatus = activeDateIsClear
    ? t('images:date_status.clear')
    : activeDateIsCloudy
      ? t('images:date_status.cloudy')
      : ''
  const activeDateStatusClass = activeDateIsClear
    ? 'is-clear'
    : activeDateIsCloudy
      ? 'is-cloudy'
      : ''
  const expandedDateIsClear = expandedDate ? clearDateSet.has(expandedDate) : false
  const expandedDateIsCloudy = expandedDate ? cloudyDateSet.has(expandedDate) : false
  const expandedDateStatus = expandedDateIsClear
    ? t('images:date_status.clear')
    : expandedDateIsCloudy
      ? t('images:date_status.cloudy')
      : ''
  const expandedDateStatusClass = expandedDateIsClear
    ? 'is-clear'
    : expandedDateIsCloudy
      ? 'is-cloudy'
      : ''

  useEffect(() => {
    const datesChanged = previousCarouselDatesKey.current !== carouselDatesKey
    previousCarouselDatesKey.current = carouselDatesKey

    setActiveIndex((current) => {
      if (carouselDates.length < 1) {
        return 0
      }

      if (datesChanged) {
        return carouselDates.length - 1
      }

      return Math.min(current, carouselDates.length - 1)
    })
  }, [carouselDates.length, carouselDatesKey])

  useEffect(() => {
    if (!expandedDate) {
      return undefined
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setExpandedDate(null)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [expandedDate])

  useEffect(() => {
    if (expandedDate && !carouselDates.includes(expandedDate)) {
      setExpandedDate(null)
    }
  }, [carouselDates, expandedDate])

  const movePrevious = () => {
    setActiveIndex((current) => Math.max(0, current - 1))
  }

  const moveNext = () => {
    setActiveIndex((current) => Math.min(carouselDates.length - 1, current + 1))
  }

  const toggleActiveDate = () => {
    if (!activeDate || selectionDisabled) {
      return
    }

    if (isActiveSelected) {
      onRemoveDate(type, activeDate)
      return
    }

    onAddDate(type, activeDate)
  }

  const toggleLatestDates = () => {
    if (!hasLatestDates) {
      return
    }

    if (latestDatesSelected) {
      onSetDates(type, dates.filter((dateValue) => !latestDateSet.has(dateValue)))
      return
    }

    onSetDates(type, latestDates)
  }

  return (
    <div className='technical-report-image-selector'>
      <div className='technical-report-image-selector__header'>
        <span>{t(`reports:technical.images.${type}`)}</span>
        <small>
          {t('reports:technical.form.images.selected_count', {
            selected: dates.length,
            total: 5,
          })}
        </small>
      </div>

      {!land && (
        <small className='technical-report-image-selector__hint'>
          {t('reports:technical.form.images.select_land_first')}
        </small>
      )}
      {land && !isLoadingDates && hasDatesInRange && (
        <div className='technical-report-image-selector__actions'>
          <button
            type='button'
            className={`technical-report-image-selector__action ${showOnlyClearImages ? 'is-active' : ''}`.trim()}
            onClick={() => setShowOnlyClearImages((current) => !current)}
          >
            <span>
              {showOnlyClearImages
                ? t('reports:technical.form.images.show_all_images')
                : t('reports:technical.form.images.filter_cloudy')}
            </span>
          </button>
          <button
            type='button'
            className={`technical-report-image-selector__action ${latestDatesSelected ? 'is-active' : ''}`.trim()}
            disabled={!hasLatestDates}
            onClick={toggleLatestDates}
          >
            <span>
              {latestDatesSelected
                ? t('reports:technical.form.images.deselect_latest_five')
                : t('reports:technical.form.images.select_latest_five')}
            </span>
          </button>
        </div>
      )}
      {land && !isLoadingDates && !hasDatesInRange && (
        <small className='technical-report-image-selector__hint'>
          {t('reports:technical.form.images.no_dates')}
        </small>
      )}
      {land && !isLoadingDates && hasDatesInRange && !hasAvailableDates && (
        <small className='technical-report-image-selector__hint'>
          {t('reports:technical.form.images.no_clear_dates')}
        </small>
      )}

      {land && isLoadingDates && (
        <small className='technical-report-image-selector__hint technical-report-image-selector__hint--loading'>
          <span>{t('reports:technical.form.images.loading_dates')}</span>
          <span className='technical-report-image-selector__spinner' />
        </small>
      )}

      {land && hasAvailableDates && (
        <div className='technical-report-image-carousel'>
          <button
            type='button'
            className='technical-report-image-carousel__nav'
            disabled={safeActiveIndex === 0}
            onClick={movePrevious}
            aria-label={t('images:previous_date')}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr026.svg' className='svg-icon svg-icon-2' />
          </button>

          <div className='technical-report-image-carousel__stage'>
            <button
              type='button'
              className='technical-report-image-carousel__side'
              disabled={!previousDate}
              onClick={movePrevious}
            >
              {previousDate ? (
                <>
                  <TechnicalReportImageThumbnail
                    acquisition={itemsByDate[previousDate]}
                    dateValue={previousDate}
                    enhanced={enhanced}
                    land={land}
                    position='side'
                    product={product}
                    thumbnailCache={thumbnailCache}
                    type={type}
                  />
                  <span>{formatInputDate(previousDate)}</span>
                </>
              ) : (
                <span />
              )}
            </button>

            <div className='technical-report-image-carousel__current'>
              <TechnicalReportImageThumbnail
                acquisition={itemsByDate[activeDate]}
                dateValue={activeDate}
                enhanced={enhanced}
                expandable
                land={land}
                onExpand={() => setExpandedDate(activeDate)}
                position='current'
                product={product}
                thumbnailCache={thumbnailCache}
                type={type}
              />
              <div className='technical-report-image-carousel__meta'>
                <strong>{activeDate ? formatInputDate(activeDate) : '-'}</strong>
                {activeDateStatus && (
                  <span className={activeDateStatusClass}>{activeDateStatus}</span>
                )}
              </div>
              <label className='technical-report-image-carousel__select'>
                <input
                  type='checkbox'
                  checked={isActiveSelected}
                  disabled={selectionDisabled}
                  onChange={toggleActiveDate}
                />
                <span>{t('reports:technical.form.images.select_current')}</span>
              </label>
              {selectionDisabled && !isActiveSelected && (
                <small className='technical-report-image-selector__hint'>
                  {t('reports:technical.form.images.max_selected')}
                </small>
              )}
            </div>

            <button
              type='button'
              className='technical-report-image-carousel__side'
              disabled={!nextDate}
              onClick={moveNext}
            >
              {nextDate ? (
                <>
                  <TechnicalReportImageThumbnail
                    acquisition={itemsByDate[nextDate]}
                    dateValue={nextDate}
                    enhanced={enhanced}
                    land={land}
                    position='side'
                    product={product}
                    thumbnailCache={thumbnailCache}
                    type={type}
                  />
                  <span>{formatInputDate(nextDate)}</span>
                </>
              ) : (
                <span />
              )}
            </button>
          </div>

          <button
            type='button'
            className='technical-report-image-carousel__nav'
            disabled={safeActiveIndex >= carouselDates.length - 1}
            onClick={moveNext}
            aria-label={t('images:next_date')}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr027.svg' className='svg-icon svg-icon-2' />
          </button>
        </div>
      )}

      {dates.length > 0 && (
        <div className='technical-report-image-selector__dates'>
          {dates.map((dateValue) => (
            <button
              type='button'
              className='technical-report-image-selector__chip'
              key={dateValue}
              onClick={() => onRemoveDate(type, dateValue)}
            >
              <span>{formatInputDate(dateValue)}</span>
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon svg-icon-5' />
            </button>
          ))}
        </div>
      )}

      {expandedDate && (
        <div
          className='technical-report-image-preview'
          role='dialog'
          aria-modal='true'
          aria-label={t('reports:technical.form.images.expanded_preview')}
          onClick={() => setExpandedDate(null)}
        >
          <div className='technical-report-image-preview__card' onClick={(event) => event.stopPropagation()}>
            <div className='technical-report-image-preview__header'>
              <div>
                <span>{t(`reports:technical.images.${type}`)}</span>
                <strong>{formatInputDate(expandedDate)}</strong>
              </div>
              <button type='button' onClick={() => setExpandedDate(null)}>
                {t('reports:technical.form.images.close_preview')}
              </button>
            </div>
            <TechnicalReportImageThumbnail
              acquisition={itemsByDate[expandedDate]}
              dateValue={expandedDate}
              enhanced={enhanced}
              land={land}
              position='preview'
              product={product}
              thumbnailCache={thumbnailCache}
              type={type}
            />
            {expandedDateStatus && (
              <span className={`technical-report-image-preview__status ${expandedDateStatusClass}`.trim()}>
                {expandedDateStatus}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const TechnicalReportLandImageCard = ({
  activeImageTypes,
  dateRange,
  imageEnhancements,
  imageDateState,
  imageSelections,
  initialOpen,
  land,
  onAddImageDate,
  onApplySelectionsToAll,
  onCopyImageDates,
  onToggleImageEnhancement,
  onRemoveImageDate,
  onSetImageDates,
  product,
  selectedLandIds = [],
  thumbnailCache,
}) => {
  const {t} = useTranslation()
  const landId = getLandId(land)
  const landSelections = getLandImageSelections(imageSelections, landId)
  const defaultActiveType = activeImageTypes.find((type) => landSelections[type].length > 0) || activeImageTypes[0]
  const [open, setOpen] = useState(initialOpen)
  const [activeType, setActiveType] = useState(defaultActiveType)
  const hasDatesToApply = landSelections[activeType].length > 0
  const availableDatesByLand = useMemo(() => selectedLandIds.reduce((accumulator, selectedLandId) => {
    const landDateState = imageDateState.byLand[selectedLandId]

    if (landDateState?.status !== 'ready') {
      return accumulator
    }

    accumulator[selectedLandId] = Array.from(new Set([
      ...(landDateState.clearDates || []),
      ...(landDateState.cloudyDates || []),
    ])).filter((dateValue) => (
      (!dateRange.from || dateValue >= dateRange.from) &&
      (!dateRange.to || dateValue <= dateRange.to)
    ))
    return accumulator
  }, {}), [dateRange.from, dateRange.to, imageDateState.byLand, selectedLandIds])
  const selectionsAppliedToAll = areImageTypeDatesAppliedToAllLands({
    availableDatesByLand,
    imageSelections,
    landIds: selectedLandIds,
    sourceLandId: landId,
    type: activeType,
  })
  const activeEnhancementType = !isPlanetHdProduct(product) && ENHANCEABLE_IMAGE_TYPES.includes(activeType)
    ? activeType
    : null
  const currentDatesState = imageDateState.byLand[landId] || {
    clearDates: [],
    cloudyDates: [],
    itemsByDate: {},
    status: 'idle',
  }
  const availableDates = useMemo(
    () => sortDatesDescending(Array.from(new Set([
      ...currentDatesState.clearDates,
      ...currentDatesState.cloudyDates,
    ]))),
    [currentDatesState.clearDates, currentDatesState.cloudyDates]
  )

  useEffect(() => {
    if (!activeImageTypes.includes(activeType)) {
      setActiveType(defaultActiveType)
    }
  }, [activeImageTypes, activeType, defaultActiveType])

  return (
    <div className='technical-report-land-images'>
      <button
        type='button'
        className='technical-report-land-images__summary'
        onClick={() => setOpen((current) => !current)}
      >
        <span className='technical-report-land-images__title'>
          <strong>{getFieldLandDisplayName(land)}</strong>
          <span>{formatHectares(getLandHa(land))}</span>
        </span>
        <span className='technical-report-land-images__counts'>
          {getTechnicalReportImageTypes(product).map((type) => (
            <span key={type}>
              {t(`reports:technical.images.${type}`)} {landSelections[type].length}/5
            </span>
          ))}
        </span>
        <KTSVG
          path={open ? '/media/icons/duotune/arrows/arr072.svg' : '/media/icons/duotune/arrows/arr071.svg'}
          className='svg-icon svg-icon-2'
        />
      </button>

      {open && (
        <div className='technical-report-land-images__body'>
          <div className='technical-report-land-images__tabs'>
            {activeImageTypes.map((type) => (
              <button
                type='button'
                className={activeType === type ? 'is-active' : ''}
                key={type}
                onClick={() => setActiveType(type)}
              >
                {t(`reports:technical.images.${type}`)}
              </button>
            ))}
          </div>
          {activeImageTypes.length > 1 && (
            <div className='technical-report-land-images__copy-dates'>
              <span>{t('reports:technical.form.images.copy_dates_from')}</span>
              <div>
                {activeImageTypes
                  .filter((type) => type !== activeType)
                  .map((type) => (
                    <button
                      type='button'
                      disabled={landSelections[type].length < 1}
                      key={type}
                      onClick={() => onCopyImageDates(landId, type, activeType)}
                    >
                      {t('reports:technical.form.images.use_dates_from', {
                        count: landSelections[type].length,
                        type: t(`reports:technical.images.${type}`),
                      })}
                    </button>
                  ))}
              </div>
            </div>
          )}
          {activeEnhancementType && (
            <div className='technical-report-image-enhancements technical-report-image-enhancements--inline'>
              <label className='technical-report-image-enhancement'>
                <span>
                  {t('reports:technical.form.images.enhance_type', {
                    type: t(`reports:technical.images.${activeEnhancementType}`),
                  })}
                </span>
                <span className='technical-report-image-enhancement__switch form-check form-switch'>
                  <input
                    type='checkbox'
                    className='form-check-input'
                    checked={Boolean(imageEnhancements?.[activeEnhancementType])}
                    onChange={() => onToggleImageEnhancement(activeEnhancementType)}
                  />
                </span>
              </label>
            </div>
          )}
          <ImageDateSelector
            availableDates={availableDates}
            clearDates={currentDatesState.clearDates}
            cloudyDates={currentDatesState.cloudyDates}
            dateRange={dateRange}
            dates={landSelections[activeType]}
            enhanced={Boolean(imageEnhancements?.[activeType])}
            itemsByDate={currentDatesState.itemsByDate}
            isLoadingDates={currentDatesState.status === 'loading'}
            key={`${landId}:${activeType}`}
            land={land}
            onAddDate={(type, dateValue) => onAddImageDate(landId, type, dateValue)}
            onRemoveDate={(type, dateValue) => onRemoveImageDate(landId, type, dateValue)}
            onSetDates={(type, dateValues) => onSetImageDates(landId, type, dateValues)}
            product={product}
            thumbnailCache={thumbnailCache}
            type={activeType}
          />
          {activeImageTypes.length > 0 && (
            <div className='technical-report-land-images__apply-group'>
              <button
                type='button'
                aria-pressed={selectionsAppliedToAll}
                className={`btn btn-sm btn-light-primary technical-report-land-images__apply ${selectionsAppliedToAll ? 'is-active' : ''}`.trim()}
                disabled={!hasDatesToApply}
                onClick={() => onApplySelectionsToAll(
                  landId,
                  activeType,
                  selectionsAppliedToAll,
                  availableDatesByLand
                )}
              >
                {selectionsAppliedToAll
                  ? t('reports:technical.form.images.remove_from_all', {
                    type: t(`reports:technical.images.${activeType}`),
                  })
                  : t('reports:technical.form.images.apply_to_all', {
                    type: t(`reports:technical.images.${activeType}`),
                  })}
              </button>
              {!selectionsAppliedToAll && hasDatesToApply && (
                <small>{t('reports:technical.form.images.apply_availability_hint')}</small>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const ReportDateSelector = ({clearDates = [], cloudyDates = [], disabled = false, hdOnly = false, label, onChange, value}) => {
  const {t} = useTranslation()
  const [openedDatePicker, setOpenedDatePicker] = useState(false)
  const clearDateSet = useMemo(() => new Set(clearDates), [clearDates])
  const cloudyDateSet = useMemo(() => new Set(cloudyDates), [cloudyDates])
  const pickerValue = value ? new Date(`${value}T00:00:00`) : new Date()
  const renderCalendarDay = (day, pickerSelectedDate, dayInCurrentMonth, dayComponent) => {
    const dateValue = format(day, 'yyyy-MM-dd')
    const isClear = clearDateSet.has(dateValue)
    const isCloudy = cloudyDateSet.has(dateValue)
    const isSelected = value === dateValue
    const dateClass = isCloudy ? 'date-with-cloud' : isClear ? 'date-with-data' : ''
    const activeClass = isSelected ? 'active' : ''
    const tooltipMessage = isCloudy
      ? `Nubosidad mayor a ${DEFAULT_CLOUDINESS_FILTER}%`
      : isClear
        ? `Nubosidad menor o igual a ${DEFAULT_CLOUDINESS_FILTER}%`
        : 'Sin imagen disponible'

    return (
      <div className={`${dateClass} ${activeClass}`.trim()} title={tooltipMessage}>
        {dayComponent}
      </div>
    )
  }

  return (
    <label>
      <span>{label}</span>
      <button
        type='button'
        className={`technical-report-date-trigger ${disabled ? 'is-disabled' : ''}`.trim()}
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpenedDatePicker(true)
          }
        }}
      >
        <KTSVG path='/media/icons/duotune/general/gen014.svg' className='svg-icon svg-icon-2' />
        <span>{value ? formatInputDate(value) : label}</span>
      </button>
      <DatePicker
        open={!disabled && openedDatePicker}
        value={pickerValue}
        autoOk
        okLabel={null}
        cancelLabel={t('common:actions.cancel')}
        renderDay={renderCalendarDay}
        onChange={(date) => {
          if (!date) {
            setOpenedDatePicker(false)
            return
          }

          onChange(format(date, 'yyyy-MM-dd'))
          setOpenedDatePicker(false)
        }}
        onOpen={() => {
          if (!disabled) {
            setOpenedDatePicker(true)
          }
        }}
        onClose={() => setOpenedDatePicker(false)}
        shouldDisableDate={(date) => date > new Date() || (hdOnly && !isPlanetHdDateAllowed(date))}
        animateYearScrolling
        DialogProps={{
          maxWidth: false,
          PaperProps: {
            className: 'view-land-date-picker-dialog',
          },
        }}
        TextFieldComponent={() => null}
      />
    </label>
  )
}

const TechnicalReportPreview = ({
  content,
  dateRange,
  imageSelections,
  notes,
  product,
  reportName,
  screen = false,
  selectedFields,
  selectedLands,
  sourceLabel = null,
  totalHa,
}) => {
  const {t} = useTranslation()
  const selectedContent = CONTENT_OPTIONS.filter((option) => content[option])
  const activeImageTypes = getTechnicalReportImageTypes(product)
    .filter((type) => content[getContentKeyForImageType(type)])

  return (
    <article
      className={`technical-report-preview ${
        screen ? 'technical-report-preview--screen' : 'technical-report-preview--print-only'
      } technical-report-print-sheet`}
    >
      <header className='technical-report-preview__cover'>
        <p className='technical-report-preview__eyebrow'>
          {t('reports:technical.preview.eyebrow')}
        </p>
        <h1>{reportName || t('reports:technical.title')}</h1>
        <div className='technical-report-preview__meta-grid'>
          <MetricCard label={t('reports:technical.preview.period')} value={`${formatInputDate(dateRange.from)} - ${formatInputDate(dateRange.to)}`} />
          <MetricCard label={t('reports:technical.preview.surface')} value={formatHectares(totalHa)} />
          <MetricCard label={t('reports:technical.preview.fields')} value={selectedFields.length} />
          <MetricCard label={t('reports:technical.preview.lands')} value={selectedLands.length} />
          {sourceLabel && (
            <MetricCard label={t('reports:technical.preview.source')} value={sourceLabel} />
          )}
        </div>
      </header>

      <section className='technical-report-preview__section'>
        <h2>{t('reports:technical.preview.scope')}</h2>
        <div className='technical-report-preview__scope'>
          {selectedFields.map((field) => (
            <div className='technical-report-preview__row' key={field.field_id}>
              <strong>{field.name}</strong>
              <ul>
                {field.lands.map((land) => (
                  <li key={getLandId(land) || land.name}>
                    {buildLandDownloadName({fieldName: field.name, landName: land.name}) || land.name}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {selectedFields.length < 1 && (
            <div className='technical-report-preview__row'>
              <strong>{t('reports:technical.preview.no_scope')}</strong>
              <span>-</span>
            </div>
          )}
        </div>
      </section>

      <section className='technical-report-preview__section'>
        <h2>{t('reports:technical.preview.content')}</h2>
        <div className='technical-report-preview__chips'>
          {selectedContent.map((option) => (
            <span key={option}>{t(`reports:technical.content.${option}`)}</span>
          ))}
        </div>
      </section>

      <section className='technical-report-preview__section technical-report-preview__media-layout'>
        <div className='technical-report-preview__general-stack'>
          {content.overview_map && (
            <div className='technical-report-preview__block'>
              <h3>{t('reports:technical.content.overview_map')}</h3>
              <p>{t('reports:technical.preview.blocks.overview_map')}</p>
            </div>
          )}
          {content.altimetry && (
            <div className='technical-report-preview__block'>
              <h3>{t('reports:technical.content.altimetry')}</h3>
              <p>{t('reports:technical.preview.blocks.altimetry')}</p>
            </div>
          )}
          {content.soil_map && (
            <div className='technical-report-preview__block'>
              <h3>{t('reports:technical.content.soil_map')}</h3>
              <p>{t('reports:technical.preview.blocks.soil_map')}</p>
            </div>
          )}
          {(content.historical_ambient || content.image_ambient) && (
            <div className='technical-report-preview__block'>
              <h3>{t('reports:technical.preview.blocks.ambients_title')}</h3>
              <p>{t('reports:technical.preview.blocks.ambients')}</p>
            </div>
          )}
        </div>
        {activeImageTypes.length > 0 && (
          <div className='technical-report-preview__block technical-report-preview__block--images'>
            <h3>{t('reports:technical.preview.blocks.images_title')}</h3>
            <div className='technical-report-preview__images'>
              {selectedLands.map((land) => {
                const landId = getLandId(land)
                const landSelections = getLandImageSelections(imageSelections, landId)

                return (
                  <div className='technical-report-preview__image-land' key={landId}>
                    <strong>{getFieldLandDisplayName(land)}</strong>
                    <div>
                      {activeImageTypes.map((type) => (
                        <span className='technical-report-preview__image-type' key={type}>
                          <b>{t(`reports:technical.images.${type}`)}</b>
                          <em>{landSelections[type].map(formatInputDate).join(', ') || '-'}</em>
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </section>

      {notes.observations && (
        <section className='technical-report-preview__section'>
          <h2>{t('reports:technical.preview.observations')}</h2>
          <p>{notes.observations}</p>
        </section>
      )}
    </article>
  )
}

const TechnicalReportListItem = ({mobile = false, onDeleteReport, report}) => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const status = report.status || 'completed'
  const canOpenReport = status === 'completed'
  const canDeleteFromList = status === 'completed' || status === 'error'
  const statusLabel = t(`reports:technical.list.status.${status}`, status)
  const sourceLabel = getTechnicalReportSourceLabel(report)
  const statusClass = {
    completed: 'badge-light-success',
    error: 'badge-light-danger',
    in_progress: 'badge-light-info',
  }[status] || 'badge-light-secondary'

  const openReport = () => {
    if (!canOpenReport) {
      openAlert(
        status === 'error' ? 'danger' : 'info',
        status === 'error'
          ? t('reports:technical.errors.title')
          : t('reports:technical.list.status.in_progress'),
        status === 'error'
          ? t('reports:technical.errors.pdf_generation')
          : t('reports:technical.list.still_creating')
      )
      return
    }

    navigate(`/gis/reports/technical/view/${report.id}`)
  }

  const onKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    openReport()
  }

  const stopAction = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const requestDelete = (event) => {
    stopAction(event)
    setConfirmDelete(true)
  }

  const cancelDelete = (event) => {
    stopAction(event)
    setConfirmDelete(false)
  }

  const deleteReport = (event) => {
    stopAction(event)
    setDeleting(true)
    onDeleteReport(report.id)
    openAlert(
      'success',
      t('reports:technical.delete.success.title'),
      t('reports:technical.delete.success.message')
    )
    setDeleting(false)
    setConfirmDelete(false)
  }

  const deleteAction = canDeleteFromList && (
    <span className='report-item__delete-flow' onClick={(event) => event.stopPropagation()}>
      {!confirmDelete && (
        <button
          type='button'
          className='btn btn-sm btn-light-danger report-item__delete'
          onClick={requestDelete}
          disabled={deleting}
        >
          {t('reports:technical.delete.button')}
        </button>
      )}
      {confirmDelete && (
        <span className='report-item__delete-confirmation'>
          <button
            type='button'
            className='btn btn-sm btn-danger report-item__delete-confirm'
            onClick={deleteReport}
            disabled={deleting}
          >
            {t('reports:technical.delete.yes_button')}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-light report-item__delete-cancel'
            onClick={cancelDelete}
            disabled={deleting}
          >
            {t('reports:technical.delete.no_button')}
          </button>
        </span>
      )}
    </span>
  )

  if (mobile) {
    return (
      <div
        role='button'
        tabIndex={0}
        onClick={openReport}
        onKeyDown={onKeyDown}
        className='reports-mobile-report-card'
      >
        <span className='reports-mobile-report-card__bar' />
        <span className='reports-mobile-report-card__copy'>
          <span className='reports-mobile-report-card__title'>{report.name}</span>
          <span className={`badge ${statusClass} reports-mobile-report-card__badge`.trim()}>
            {statusLabel}
          </span>
          {sourceLabel && <small>{sourceLabel}</small>}
        </span>
        {deleteAction}
        {canOpenReport && (
          <span className='reports-mobile-report-card__action'>
            <KTSVG path='/media/icons/duotune/arrows/arr024.svg' className='svg-icon svg-icon-2' />
          </span>
        )}
      </div>
    )
  }

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={openReport}
      onKeyDown={onKeyDown}
      className='d-flex report-item align-items-center p-5'
    >
      <span className='bullet bullet-vertical h-40px bg-primary me-5 report-item__bullet'></span>
      <div className='flex-grow-1 report-item__content'>
        <span className='text-gray-800 text-hover-primary fw-bold fs-6 report-item__title'>
          {report.name}
        </span>
        <p className='p-0 d-block report-item__status'>
          <span className={`badge ${statusClass}`.trim()}>{statusLabel}</span>
          {sourceLabel && <span className='badge badge-light-primary ms-2'>{sourceLabel}</span>}
        </p>
      </div>
      {deleteAction}
      {canOpenReport && (
        <span className='btn btn-sm btn-light-primary fs-8 fw-bold report-item__cta'>
          <KTSVG path='/media/icons/duotune/arrows/arr024.svg' />
        </span>
      )}
    </div>
  )
}

export const TechnicalReportList = ({mobile = false, onDeleteReport, reports = []}) => {
  const {t} = useTranslation()
  const navigate = useNavigate()

  if (mobile) {
    return (
      <div className='reports-mobile-shell displayable'>
        <div className='card shadow-sm right-card-data reports-mobile-card'>
          <div className='reports-mobile-card__header'>
            <button
              type='button'
              className='btn btn-sm btn-light reports-mobile-card__back'
              onClick={() => navigate('/gis/reports')}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon svg-icon-2' />
              <span>{t('common:navigation.back_reports')}</span>
            </button>
            <div className='reports-mobile-card__eyebrow'>{t('reports:selector.report_label')}</div>
            <h2 className='reports-mobile-card__title'>{t('reports:technical.title')}</h2>
            <button
              type='button'
              className='btn btn-sm btn-primary reports-mobile-card__primary-action'
              onClick={() => navigate('/gis/reports/technical/new')}
            >
              {t('reports:technical.buttons.new_report')}
            </button>
          </div>
          <div className='reports-mobile-card__body'>
            {reports.length < 1 && (
              <div className='reports-mobile-card__empty'>
                {t('reports:technical.list.no_reports')}
              </div>
            )}
            {reports.map((report) => (
              <TechnicalReportListItem
                key={report.id}
                mobile
                onDeleteReport={onDeleteReport}
                report={report}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='card shadow-sm field-view-container displayable right-card-data technical-report-list-card'>
      <div className='card-header collapsible technical-report-list-card__header'>
        <div className='technical-report-list-card__heading'>
          <button
            type='button'
            className='btn btn-sm btn-light reports-mobile-card__back technical-report-list-card__back'
            onClick={() => navigate('/gis/reports')}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon svg-icon-2' />
            <span>{t('common:navigation.back_reports')}</span>
          </button>
          <h4 className='card-title cursor-pointer technical-report-list-card__title' data-bs-toggle='collapse' data-bs-target='#kt_technical_report_list'>
            {t('reports:technical.title')}
          </h4>
        </div>
        <div className='card-toolbar'>
          <button
            type='button'
            className='btn btn-sm btn-light-primary gis-toolbar-action'
            onClick={() => navigate('/gis/reports/technical/new')}
          >
            {t('reports:technical.buttons.new_report')}
          </button>
        </div>
      </div>
      <div id='kt_technical_report_list' className='collapse show'>
        <div className={`card-body ${reports.length < 1 ? 'p-5' : 'p-0'}`}>
          {reports.length < 1 && (
            <p className='m-0 reports-empty-state'>{t('reports:technical.list.no_reports')}</p>
          )}
          {reports.map((report) => (
            <TechnicalReportListItem
              key={report.id}
              onDeleteReport={onDeleteReport}
              report={report}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const resolveTechnicalReportScope = (fields = [], selected = {}) => {
  const selectedLandIdSet = new Set(
    Object.entries(selected)
      .filter(([, value]) => Boolean(value))
      .map(([landId]) => String(landId))
  )
  const selectedLands = []
  const selectedFields = fields
    .map((field) => {
      const lands = (field.lands || []).filter((land) => {
        const landId = getLandId(land)
        const isSelected = landId != null && selectedLandIdSet.has(String(landId))

        if (isSelected) {
          selectedLands.push({...land, field})
        }

        return isSelected
      })

      return {...field, lands}
    })
    .filter((field) => field.lands.length > 0)
  const resolvedLandIdSet = new Set(selectedLands.map((land) => String(getLandId(land))))

  return {
    missingLandIds: [...selectedLandIdSet].filter((landId) => !resolvedLandIdSet.has(landId)),
    selectedFields,
    selectedLands,
    totalHa: selectedLands.reduce((sum, land) => sum + getLandHa(land), 0),
  }
}

const TechnicalReportDetailMetric = ({label, value}) => (
  <div className='technical-report-detail-metric'>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
)

const TechnicalReportDetailBody = ({
  activeImageTypes,
  mobile = false,
  report,
  selectedFields,
  selectedLands,
  totalHa,
}) => {
  const {t} = useTranslation()
  const selectedContent = CONTENT_OPTIONS.filter((option) => report.content[option])
  const sourceLabel = getTechnicalReportSourceLabel(report)

  return (
    <div
      className={`technical-report-detail__body${
        mobile ? ' technical-report-detail__body--mobile' : ' modal-body'
      }`}
    >
      <section className='technical-report-detail__section'>
        <h6>{t('reports:technical.view.sections.summary')}</h6>
        <div className='technical-report-detail__metrics'>
          <TechnicalReportDetailMetric
            label={t('reports:technical.preview.period')}
            value={`${formatInputDate(report.dateRange.from)} - ${formatInputDate(report.dateRange.to)}`}
          />
          <TechnicalReportDetailMetric
            label={t('reports:technical.preview.surface')}
            value={formatHectares(totalHa)}
          />
          <TechnicalReportDetailMetric
            label={t('reports:technical.preview.fields')}
            value={selectedFields.length}
          />
          <TechnicalReportDetailMetric
            label={t('reports:technical.preview.lands')}
            value={selectedLands.length}
          />
          {sourceLabel && (
            <TechnicalReportDetailMetric
              label={t('reports:technical.preview.source')}
              value={sourceLabel}
            />
          )}
        </div>
      </section>

      <section className='technical-report-detail__section'>
        <h6>{t('reports:technical.preview.scope')}</h6>
        <div className='technical-report-detail__scope'>
          {selectedFields.map((field) => (
            <div
              className='technical-report-detail__field'
              key={field.field_id || field.id || field.name}
            >
              <strong>{field.name}</strong>
              <div>
                {field.lands.map((land) => (
                  <span key={getLandId(land) || land.name}>
                    <b>{buildLandDownloadName({fieldName: field.name, landName: land.name}) || land.name}</b>
                    <em>{formatHectares(getLandHa(land))}</em>
                  </span>
                ))}
              </div>
            </div>
          ))}
          {selectedFields.length < 1 && (
            <p className='technical-report-detail__empty'>
              {t('reports:technical.preview.no_scope')}
            </p>
          )}
        </div>
      </section>

      <section className='technical-report-detail__section'>
        <h6>{t('reports:technical.preview.content')}</h6>
        <div className='technical-report-detail__chips'>
          {selectedContent.map((option) => (
            <span key={option}>{t(`reports:technical.content.${option}`)}</span>
          ))}
        </div>
      </section>

      {activeImageTypes.length > 0 && (
        <section className='technical-report-detail__section'>
          <h6>{t('reports:technical.view.sections.images')}</h6>
          <div className='technical-report-detail__images'>
            {selectedLands.map((land) => {
              const landId = getLandId(land)
              const landSelections = getLandImageSelections(report.imageSelections, landId)

              return (
                <div className='technical-report-detail__image-row' key={landId}>
                  <strong>{getFieldLandDisplayName(land)}</strong>
                  <div>
                    {activeImageTypes.map((type) => (
                      <span key={type}>
                        <b>{t(`reports:technical.images.${type}`)}</b>
                        <em>{landSelections[type].map(formatInputDate).join(', ') || '-'}</em>
                      </span>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {report.notes.observations && (
        <section className='technical-report-detail__section'>
          <h6>{t('reports:technical.preview.observations')}</h6>
          <p className='technical-report-detail__observations'>{report.notes.observations}</p>
        </section>
      )}
    </div>
  )
}

const TechnicalReportDetail = ({
  mobile = false,
  onClose,
  onDeleteReport,
  report,
}) => {
  const {currentUser} = useAuth()
  const {t} = useTranslation()
  const {groupedOptions} = useSearchContext()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const downloadStartedRef = useRef(false)
  const fields = useMemo(
    () => (groupedOptions || []).filter((field) => (field.lands || []).length > 0),
    [groupedOptions]
  )
  const {
    missingLandIds,
    selectedFields,
    selectedLands,
    totalHa,
  } = useMemo(
    () => resolveTechnicalReportScope(fields, report.selected),
    [fields, report.selected]
  )
  const activeImageTypes = useMemo(
    () => getTechnicalReportImageTypes(getTechnicalReportProduct(report))
      .filter((type) => report.content[getContentKeyForImageType(type)]),
    [report]
  )
  const reportProduct = getTechnicalReportProduct(report)
  const hasStoredHdAcquisitions = !isPlanetHdProduct(reportProduct) ||
    hasCompleteStoredHdAcquisitionSelection({
      imageSelections: report.imageSelections,
      imageTypes: activeImageTypes,
      selectedLandIds: selectedLands.map(getLandId).filter(Boolean),
    })
  const canDownload = selectedLands.length > 0 &&
    missingLandIds.length < 1 &&
    hasStoredHdAcquisitions &&
    (!isPlanetHdProduct(reportProduct) || PLANET_HD_V1_ENABLED)

  const downloadPdf = async () => {
    if (!canDownload || isDownloading || downloadStartedRef.current) {
      openAlert(
        'danger',
        t('reports:technical.errors.title'),
        t('reports:technical.view.scope_unavailable', {count: missingLandIds.length})
      )
      return
    }

    downloadStartedRef.current = true
    setIsDownloading(true)

    try {
      const selectedContent = CONTENT_OPTIONS
        .filter((option) => report.content[option])
        .map((option) => t(`reports:technical.content.${option}`))
      const selectedImages = activeImageTypes.length > 0
        ? selectedLands.map((land) => {
          const landId = getLandId(land)
          const landSelections = getLandImageSelections(report.imageSelections, landId)

          return {
            landName: getFieldLandDisplayName(land),
            images: activeImageTypes.map((type) => ({
              label: t(`reports:technical.images.${type}`),
              count: landSelections[type].length,
              dates: landSelections[type].map(formatInputDate),
            })),
          }
        })
        : []
      const visualSections = await buildTechnicalReportVisualSections({
        currentUser,
        content: report.content,
        imageEnhancements: report.imageEnhancements,
        imageSelections: report.imageSelections,
        product: getTechnicalReportProduct(report),
        labels: {
          class: t('ambient:legend.class'),
          content: CONTENT_OPTIONS.reduce((accumulator, option) => {
            accumulator[option] = t(`reports:technical.content.${option}`)
            return accumulator
          }, {}),
          images: IMAGE_TYPES.reduce((accumulator, type) => {
            accumulator[type] = t(`reports:technical.images.${type}`)
            return accumulator
          }, {}),
          enhanced: t('images:legend.enhanced'),
          legend: t('images:legend.title'),
          soilMap: {
            coverage: t('images:soil_map.coverage'),
            dataset: t('images:soil_map.dataset'),
            memory: t('images:soil_map.memory'),
            noCoverage: t('images:soil_map.no_coverage'),
            scale: t('images:soil_map.scale'),
            source: t('images:soil_map.source'),
            sourceUnavailable: t('images:soil_map.source_unavailable'),
            title: t('images:legend.title'),
            unknownUnit: t('images:soil_map.unknown_unit'),
          },
          unavailable: t('reports:technical.preview.unavailable', {defaultValue: 'No disponible'}),
        },
        selectedLands,
      })

      await downloadTechnicalReportPdf({
        content: selectedContent,
        dateRange: {
          from: formatInputDate(report.dateRange.from),
          to: formatInputDate(report.dateRange.to),
        },
        imageCounts: selectedImages,
        labels: {
          content: t('reports:technical.preview.content'),
          eyebrow: t('reports:technical.preview.eyebrow'),
          fields: t('reports:technical.preview.fields'),
          generatedAt: t('reports:technical.preview.generated_at'),
          generatedAtValue: format(new Date(), 'dd/MM/yyyy'),
          images: t('reports:technical.form.images.title'),
          lands: t('reports:technical.preview.lands'),
          noScope: t('reports:technical.preview.no_scope'),
          observations: t('reports:technical.preview.observations'),
          period: t('reports:technical.preview.period'),
          scope: t('reports:technical.preview.scope'),
          surface: t('reports:technical.preview.surface'),
          source: t('reports:technical.preview.source'),
        },
        notes: report.notes,
        reportName: report.name,
        selectedFields: selectedFields.map((field) => ({
          ...field,
          lands: field.lands.map((land) => ({
            ...land,
            ha: formatHectares(getLandHa(land)),
          })),
        })),
        selectedLands,
        totalHa: formatHectares(totalHa),
        sourceLabel: getTechnicalReportSourceLabel(report),
        visualSections,
      })
    } catch (error) {
      console.error(error)
      openAlert(
        'danger',
        t('reports:technical.errors.title'),
        t('reports:technical.errors.pdf_generation')
      )
    } finally {
      downloadStartedRef.current = false
      setIsDownloading(false)
    }
  }

  const deleteReport = () => {
    setDeleting(true)
    onDeleteReport(report.id)
    openAlert(
      'success',
      t('reports:technical.delete.success.title'),
      t('reports:technical.delete.success.message')
    )
    setDeleting(false)
    setConfirmDelete(false)
    onClose()
  }

  const body = (
    <TechnicalReportDetailBody
      activeImageTypes={activeImageTypes}
      mobile={mobile}
      report={report}
      selectedFields={selectedFields}
      selectedLands={selectedLands}
      totalHa={totalHa}
    />
  )

  const actions = (
    <>
      <button
        type='button'
        className={mobile ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-light-primary'}
        disabled={!canDownload || isDownloading}
        onClick={downloadPdf}
      >
        {isDownloading
          ? t('reports:technical.buttons.downloading_pdf')
          : t('reports:technical.buttons.download_pdf')}
      </button>
      <button
        type='button'
        className='btn btn-sm btn-light-danger'
        onClick={() => setConfirmDelete(true)}
      >
        {t('reports:technical.delete.button')}
      </button>
    </>
  )

  return (
    <>
      {mobile ? (
        <div className='reports-mobile-shell displayable'>
          <div className='card shadow-sm right-card-data reports-mobile-card reports-mobile-card--detail technical-report-detail'>
            <div className='reports-mobile-card__header'>
              <button
                type='button'
                className='btn btn-sm btn-light reports-mobile-card__back'
                onClick={onClose}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon svg-icon-2' />
                <span>{t('common:navigation.back_reports')}</span>
              </button>
              <div className='reports-mobile-card__eyebrow'>
                {t('reports:technical.view.eyebrow')}
              </div>
              <h2 className='reports-mobile-card__title'>{report.name}</h2>
              <span className='badge badge-light-success technical-report-detail__status'>
                {t('reports:technical.list.status.completed')}
              </span>
              <div className='technical-report-detail__mobile-actions'>{actions}</div>
              {missingLandIds.length > 0 && (
                <p className='technical-report-detail__warning'>
                  {t('reports:technical.view.scope_unavailable', {count: missingLandIds.length})}
                </p>
              )}
            </div>
            <div className='reports-mobile-card__body reports-mobile-card__body--detail'>
              {body}
            </div>
          </div>
        </div>
      ) : (
        <div className='modal-content technical-report-detail'>
          <div className='modal-header technical-report-detail__header'>
            <div className='technical-report-detail__heading'>
              <h5 className='modal-title'>{t('reports:technical.title')}</h5>
              <div>
                <strong>{report.name}</strong>
                <span className='badge badge-light-success technical-report-detail__status'>
                  {t('reports:technical.list.status.completed')}
                </span>
              </div>
            </div>
            <div className='technical-report-detail__actions'>
              {actions}
              <button
                type='button'
                className='btn btn-icon btn-sm btn-active-light-primary'
                onClick={onClose}
                aria-label={t('reports:technical.view.close')}
                title={t('reports:technical.view.close')}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon svg-icon-2x' />
              </button>
            </div>
          </div>
          {missingLandIds.length > 0 && (
            <p className='technical-report-detail__warning'>
              {t('reports:technical.view.scope_unavailable', {count: missingLandIds.length})}
            </p>
          )}
          {body}
        </div>
      )}
      {confirmDelete && (
        <ReportDeleteConfirmation
          title={t('reports:technical.delete.question')}
          confirmLabel={t('reports:technical.delete.yes_button')}
          cancelLabel={t('reports:technical.delete.no_button')}
          deleting={deleting}
          onConfirm={deleteReport}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </>
  )
}

const TechnicalReportDetailContainer = ({onDeleteReport, reports = []}) => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const {id} = useParams()
  const report = reports.find((item) => item.id === id)
  const close = () => navigate('/gis/reports/technical')

  useEffect(() => {
    if (!report) {
      close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report])

  if (!report) {
    return null
  }

  if (isMobile) {
    return (
      <TechnicalReportDetail
        mobile
        onClose={close}
        onDeleteReport={onDeleteReport}
        report={report}
      />
    )
  }

  return (
    <Modal open={true} setOpen={close} focus={false}>
      <div className='modal-dialog modal-dialog-centered modal-xl technical-report-detail-modal'>
        <TechnicalReportDetail
          onClose={close}
          onDeleteReport={onDeleteReport}
          report={report}
        />
      </div>
    </Modal>
  )
}

const TechnicalReportEditor = ({initialReport = null, mobile = false, onClose, onSaveReport}) => {
  const {currentUser} = useAuth()
  const historicalHdAllowed = canAccessHistoricalHd(currentUser)
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {errorHandler} = useErrorHandler()
  const {groupedOptions} = useSearchContext()
  const [reportName, setReportName] = useState(() =>
    initialReport?.name || t('reports:technical.form.name.default', {date: format(new Date(), 'dd-MM-yyyy')})
  )
  const [dateRange, setDateRange] = useState(() => ({
    from: initialReport?.dateRange?.from || '',
    to: initialReport?.dateRange?.to || getTodayInputValue(),
  }))
  const [reportId] = useState(() => initialReport?.id || createTechnicalReportId())
  const [product, setProductState] = useState(() =>
    getTechnicalReportProduct(initialReport || {}) || REMOTE_SENSING_PRODUCTS.SENTINEL
  )
  const [selected, setSelected] = useState(() => normalizeStoredSelected(initialReport?.selected))
  const [storedContent, setContent] = useState(() =>
    normalizeStoredContent(initialReport?.content, {soilMapDefault: !initialReport})
  )
  const content = isPlanetHdProduct(product) && !historicalHdAllowed
    ? {...storedContent, historical_ambient: false}
    : storedContent
  const [imageSelections, setImageSelections] = useState(() =>
    normalizeStoredImageSelections(initialReport?.imageSelections)
  )
  const [imageEnhancements, setImageEnhancements] = useState(() =>
    createInitialImageEnhancements(initialReport?.imageEnhancements)
  )
  const imageThumbnailCache = useMemo(() => new Map(), [])
  const [imageDateState, setImageDateState] = useState({
    byLand: {},
    status: 'idle',
  })
  const [notes, setNotes] = useState(() => ({
    observations: initialReport?.notes?.observations || '',
  }))
  const [isDownloading, setIsDownloading] = useState(false)
  const generationStartedRef = useRef(false)
  const editorSourceLabel = PLANET_HD_V1_ENABLED
    ? getTechnicalReportSourceLabel({product})
    : getTechnicalReportSourceLabel(initialReport || {})

  const fields = useMemo(
    () => (groupedOptions || []).filter((field) => (field.lands || []).length > 0),
    [groupedOptions]
  )

  const activeImageTypes = useMemo(
    () => getTechnicalReportImageTypes(product)
      .filter((type) => content[getContentKeyForImageType(type)]),
    [content, product]
  )

  const selectedLands = useMemo(() => {
    const lands = []

    fields.forEach((field) => {
      field.lands.forEach((land) => {
        if (selected[getLandId(land)]) {
          lands.push({...land, field})
        }
      })
    })

    return lands
  }, [fields, selected])

  const selectedFields = useMemo(() => {
    return fields
      .map((field) => ({
        ...field,
        lands: field.lands.filter((land) => selected[getLandId(land)]),
      }))
      .filter((field) => field.lands.length > 0)
  }, [fields, selected])
  const totalHa = useMemo(
    () => selectedLands.reduce((sum, land) => sum + getLandHa(land), 0),
    [selectedLands]
  )
  const selectedLandIds = useMemo(
    () => selectedLands.map(getLandId).filter(Boolean).sort(),
    [selectedLands]
  )
  const selectedLandIdsKey = useMemo(() => selectedLandIds.join('|'), [selectedLandIds])
  const hasSelectedContent = useMemo(
    () => CONTENT_OPTIONS.some((option) => content[option]),
    [content]
  )
  const hasRequiredImageDates = useMemo(
    () => activeImageTypes.every((type) =>
      selectedLands.some((land) => {
        const landId = getLandId(land)
        return getLandImageSelections(imageSelections, landId)[type].length > 0
      })
    ),
    [activeImageTypes, imageSelections, selectedLands]
  )
  const hdAcquisitionSelectionsComplete = useMemo(() => (
    !isPlanetHdProduct(product) || hasCompleteHdAcquisitionSelection({
      imageDateState,
      imageSelections,
      imageTypes: activeImageTypes,
      selectedLandIds,
    })
  ), [activeImageTypes, imageDateState, imageSelections, product, selectedLandIds])
  const sourceMeta = useMemo(() => buildTechnicalReportSourceMeta({
    imageDateState,
    imageSelections,
    product,
  }), [imageDateState, imageSelections, product])
  const persistedImageSelections = useMemo(() => withTechnicalReportSource(
    normalizeStoredImageSelections(imageSelections),
    sourceMeta
  ), [imageSelections, sourceMeta])
  const canDownload = Boolean(
    reportName.trim() &&
    dateRange.from &&
    dateRange.to &&
    dateRange.from <= dateRange.to &&
    selectedLands.length > 0 &&
    selectedLands.length <= MAX_TECHNICAL_REPORT_LANDS &&
    hasSelectedContent &&
    hasRequiredImageDates &&
    imageDateState.status === 'ready' &&
    hdAcquisitionSelectionsComplete &&
    (!isPlanetHdProduct(product) || (isPlanetHdDateAllowed(dateRange.from) && isPlanetHdDateAllowed(dateRange.to))) &&
    (!isPlanetHdProduct(product) || PLANET_HD_V1_ENABLED)
  )
  const hdImageDateRangeKey = isPlanetHdProduct(product)
    ? `${dateRange.from}:${dateRange.to}`
    : ''

  useEffect(() => {
    return () => {
      imageThumbnailCache.forEach((imageUrl) => {
        window.URL.revokeObjectURL(imageUrl)
      })
      imageThumbnailCache.clear()
    }
  }, [imageThumbnailCache])

  useEffect(() => {
    let cancelled = false
    const landIds = selectedLandIdsKey ? selectedLandIdsKey.split('|') : []
    const [hdDateFrom, hdDateTo] = hdImageDateRangeKey.split(':')

    if (landIds.length < 1) {
      setImageDateState({
        byLand: {},
        status: 'idle',
      })
      return () => {
        cancelled = true
      }
    }

    if (isPlanetHdProduct(product) && hdDateFrom && !isPlanetHdDateAllowed(hdDateFrom)) {
      setImageDateState({byLand: {}, status: 'outside_archive'})
      return () => {
        cancelled = true
      }
    }

    const loadImageDates = async () => {
      setImageDateState((current) => ({
        ...current,
        byLand: landIds.reduce((accumulator, landId) => {
          accumulator[landId] = current.byLand[landId] || {
            clearDates: [],
            cloudyDates: [],
            status: 'loading',
          }
          accumulator[landId] = {
            ...accumulator[landId],
            status: 'loading',
          }
          return accumulator
        }, {}),
        status: 'loading',
      }))

      if (isPlanetHdProduct(product) && !PLANET_HD_V1_ENABLED) {
        setImageDateState({
          byLand: landIds.reduce((accumulator, landId) => {
            accumulator[landId] = {
              clearDates: [],
              cloudyDates: [],
              itemsByDate: {},
              status: 'feature_disabled',
            }
            return accumulator
          }, {}),
          status: 'feature_disabled',
        })
        return
      }

      if (PLANET_HD_V1_ENABLED && isPlanetHdProduct(product)) {
        const source = normalizeRemoteSensingSource({product, layer: 'ndvi'})
        const requests = landIds.map(async (landId) => {
          const accessResponse = await getLandHdAccess(landId, {
            operation: 'technical_report_hd',
            product,
          })

          if (!accessResponse?.ok) {
            throw new Error('planet-hd-access-load-failed')
          }

          const access = accessResponse.data
          if (!canUsePlanetHd(access)) {
            return {landId, status: 'access_not_ready'}
          }

          const acquisitionResponse = await searchLandHdAcquisitions(landId, {
            ...buildPlanetHdAcquisitionQuery({
              cloudPercent: 100,
              dateFrom: hdDateFrom,
              dateTo: hdDateTo,
            }),
          })
          if (!acquisitionResponse?.ok) {
            throw new Error('planet-hd-acquisitions-load-failed')
          }

          const items = selectBestPlanetHdItemsByDate(
            normalizeRemoteSensingDateItems(acquisitionResponse.data, source)
              .filter(isRemoteSensingDateVisualizable)
          )
          const itemsByDate = {}
          const clearDates = new Set()
          const cloudyDates = new Set()

          items.forEach((item) => {
            itemsByDate[item.date] = item
            const target = item.cloudPercent != null && item.cloudPercent > DEFAULT_CLOUDINESS_FILTER
              ? cloudyDates
              : clearDates
            target.add(item.date)
          })

          return {
            clearDates: sortDatesDescending(
              Array.from(clearDates).filter((dateValue) => !cloudyDates.has(dateValue))
            ),
            cloudyDates: sortDatesDescending(Array.from(cloudyDates)),
            itemsByDate,
            landId,
            status: 'ready',
          }
        })
        const results = await Promise.allSettled(requests)

        if (cancelled) {
          return
        }

        const byLand = {}
        let overallStatus = 'ready'
        results.forEach((result, index) => {
          const landId = landIds[index]
          if (result.status === 'rejected') {
            overallStatus = 'error'
            byLand[landId] = {clearDates: [], cloudyDates: [], itemsByDate: {}, status: 'error'}
            return
          }

          byLand[landId] = {
            clearDates: result.value.clearDates || [],
            cloudyDates: result.value.cloudyDates || [],
            itemsByDate: result.value.itemsByDate || {},
            status: result.value.status,
          }
          if (result.value.status === 'access_not_ready' && overallStatus !== 'error') {
            overallStatus = 'access_not_ready'
          }
        })
        setImageDateState({byLand, status: overallStatus})
        return
      }

      const requests = landIds.flatMap((landId) => [
        getLandDates(landId, {
          cloud_coverage_filter: 'below',
          cloud_coverage: DEFAULT_CLOUDINESS_FILTER,
        }).then((res) => ({
          dates: res?.ok ? res.data : [],
          landId,
          type: 'clear',
        })),
        getLandDates(landId, {
          cloud_coverage_filter: 'above',
          cloud_coverage: DEFAULT_CLOUDINESS_FILTER,
        }).then((res) => ({
          dates: res?.ok ? res.data : [],
          landId,
          type: 'cloudy',
        })),
      ])

      const results = await Promise.allSettled(requests)

      if (cancelled) {
        return
      }

      const datesByLand = landIds.reduce((accumulator, landId) => {
        accumulator[landId] = {
          clearDates: new Set(),
          cloudyDates: new Set(),
        }
        return accumulator
      }, {})

      results.forEach((result) => {
        if (result.status !== 'fulfilled') {
          return
        }

        const targetLand = datesByLand[result.value.landId]

        if (!targetLand) {
          return
        }

        const target = result.value.type === 'clear' ? targetLand.clearDates : targetLand.cloudyDates
        ;(result.value.dates || []).forEach((dateValue) => {
          target.add(dateValue)
        })
      })

      setImageDateState({
        byLand: Object.entries(datesByLand).reduce((accumulator, [landId, value]) => {
          const cloudyDateValues = sortDatesDescending(Array.from(value.cloudyDates))
          const clearDateValues = sortDatesDescending(
            Array.from(value.clearDates).filter((dateValue) => !value.cloudyDates.has(dateValue))
          )

          accumulator[landId] = {
            clearDates: clearDateValues,
            cloudyDates: cloudyDateValues,
            itemsByDate: {},
            status: 'ready',
          }
          return accumulator
        }, {}),
        status: 'ready',
      })
    }

    loadImageDates().catch(() => {
      if (cancelled) {
        return
      }

      setImageDateState({
        byLand: landIds.reduce((accumulator, landId) => {
          accumulator[landId] = {
            clearDates: [],
            cloudyDates: [],
            itemsByDate: {},
            status: 'error',
          }
          return accumulator
        }, {}),
        status: 'error',
      })
    })

    return () => {
      cancelled = true
    }
  }, [hdImageDateRangeKey, product, selectedLandIdsKey])

  useEffect(() => {
    if (imageDateState.status === 'loading') {
      return
    }

    setImageSelections((current) => {
      let changed = false
      const next = {...current}
      const selectedLandIdSet = new Set(selectedLandIds)

      Object.entries(current).forEach(([landId, selections]) => {
        if (!selectedLandIdSet.has(landId)) {
          return
        }

        let landChanged = false
        const landDateState = imageDateState.byLand[landId] || {
          clearDates: [],
          cloudyDates: [],
        }

        if (landDateState.status !== 'ready') {
          return
        }

        const availableDateSet = new Set([
          ...landDateState.clearDates,
          ...landDateState.cloudyDates,
        ])
        const currentSelections = getLandImageSelections(current, landId)
        const nextSelections = {...currentSelections}

        IMAGE_TYPES.forEach((type) => {
          const filteredDates = currentSelections[type].filter((dateValue) => {
            if (!availableDateSet.has(dateValue)) {
              return false
            }

            if (dateRange.from && dateValue < dateRange.from) {
              return false
            }

            if (dateRange.to && dateValue > dateRange.to) {
              return false
            }

            return true
          })

          if (filteredDates.length !== currentSelections[type].length) {
            nextSelections[type] = filteredDates
            landChanged = true
            changed = true
          }
        })

        if (landChanged) {
          next[landId] = nextSelections
        }
      })

      return changed ? next : current
    })
  }, [dateRange.from, dateRange.to, imageDateState.byLand, imageDateState.status, selectedLandIds])

  const selectProduct = (nextProduct) => {
    if (
      !PLANET_HD_V1_ENABLED ||
      nextProduct === product ||
      !Object.values(REMOTE_SENSING_PRODUCTS).includes(nextProduct)
    ) {
      return
    }

    imageThumbnailCache.forEach((imageUrl) => window.URL.revokeObjectURL(imageUrl))
    imageThumbnailCache.clear()
    setProductState(nextProduct)
    setDateRange({from: '', to: getTodayInputValue()})
    setImageSelections({})
    setImageDateState({byLand: {}, status: 'idle'})
    setContent((current) => ({
      ...current,
      ndre_images: false,
    }))
  }

  const closeEditor = () => {
    if (onClose) {
      onClose()
      return
    }

    navigate('/gis/reports/technical')
  }

  const toggleLand = (land) => {
    const landId = getLandId(land)

    if (!selected[landId] && selectedLands.length >= MAX_TECHNICAL_REPORT_LANDS) {
      openAlert(
        'danger',
        t('reports:technical.errors.title'),
        t('reports:technical.errors.max_lands', {max: MAX_TECHNICAL_REPORT_LANDS})
      )
      return
    }

    setSelected((current) => ({
      ...current,
      [landId]: !current[landId],
    }))
  }

  const toggleField = (field) => {
    const lands = field.lands || []
    const allSelected = lands.length > 0 && lands.every((land) => selected[getLandId(land)])

    if (!allSelected) {
      const newSelectedCount = lands.filter((land) => !selected[getLandId(land)]).length

      if (selectedLands.length + newSelectedCount > MAX_TECHNICAL_REPORT_LANDS) {
        openAlert(
          'danger',
          t('reports:technical.errors.title'),
          t('reports:technical.errors.max_lands', {max: MAX_TECHNICAL_REPORT_LANDS})
        )
        return
      }
    }

    setSelected((current) => {
      const next = {...current}
      lands.forEach((land) => {
        next[getLandId(land)] = !allSelected
      })
      return next
    })
  }

  const toggleContent = (key) => {
    setContent((current) => ({
      ...current,
      [key]: !current[key],
    }))
  }

  const toggleImageEnhancement = (type) => {
    if (!ENHANCEABLE_IMAGE_TYPES.includes(type)) {
      return
    }

    setImageEnhancements((current) => ({
      ...current,
      [type]: !current[type],
    }))
  }

  const addImageDate = (landId, type, value) => {
    setImageSelections((current) => {
      const landSelections = getLandImageSelections(current, landId)

      if (!value || landSelections[type].includes(value) || landSelections[type].length >= 5) {
        return current
      }

      return {
        ...current,
        [landId]: {
          ...landSelections,
          [type]: [...landSelections[type], value].sort((left, right) => new Date(right) - new Date(left)),
        },
      }
    })
  }

  const removeImageDate = (landId, type, value) => {
    setImageSelections((current) => {
      const landSelections = getLandImageSelections(current, landId)

      return {
        ...current,
        [landId]: {
          ...landSelections,
          [type]: landSelections[type].filter((dateValue) => dateValue !== value),
        },
      }
    })
  }

  const setImageDates = (landId, type, values = []) => {
    setImageSelections((current) => {
      const landSelections = getLandImageSelections(current, landId)
      const dates = Array.from(new Set(values.filter(Boolean)))
        .slice(0, 5)
        .sort((left, right) => new Date(right) - new Date(left))

      return {
        ...current,
        [landId]: {
          ...landSelections,
          [type]: dates,
        },
      }
    })
  }

  const copyImageDates = (landId, sourceType, targetType) => {
    setImageSelections((current) => copyImageTypeDatesForLand({
      imageSelections: current,
      landId,
      sourceType,
      targetType,
    }))
  }

  const applyImageSelectionsToAllLands = (
    sourceLandId,
    type,
    remove = false,
    availableDatesByLand = {}
  ) => {
    setImageSelections((current) => applyImageTypeDatesToAllLands({
      availableDatesByLand,
      imageSelections: current,
      landIds: selectedLandIds,
      remove,
      sourceLandId,
      type,
    }))
  }

  const downloadSavedTechnicalReportPdf = async (storedReport, fallbackBlob) => {
    const downloadPath = storedReport?.artifacts?.pdf?.downloadPath

    if (!downloadPath) {
      return fallbackBlob
    }

    const res = await errorHandler(ReportService.downloadTechnicalReportArtifact(downloadPath), [400, 404, 422, 500])

    if (res?.ok) {
      return res.data
    }

    return fallbackBlob
  }

  const saveReportBeforeDownload = async (reportId, pdfBlob, filename) => {
    const draft = {
      id: reportId,
      createdAt: initialReport?.createdAt,
      name: reportName.trim(),
      dateRange,
      selected,
      content,
      imageEnhancements,
      imageSelections: persistedImageSelections,
      notes,
      totalHa,
      landIds: selectedLandIds,
    }
    const payload = buildTechnicalReportApiPayload(draft, selectedLandIds)
    const res = await errorHandler(ReportService.saveTechnicalReport(payload), [400, 404, 422, 500])

    if (res?.ok) {
      const savedReport = normalizeStoredTechnicalReport(res.data)

      if (!savedReport) {
        return false
      }

      const upload = await errorHandler(
        ReportService.uploadTechnicalReportPdf(savedReport.id, pdfBlob, filename),
        [400, 404, 422, 500]
      )

      if (upload?.ok) {
        const reportWithArtifact = normalizeStoredTechnicalReport(upload.data)

        if (!reportWithArtifact) {
          return false
        }

        const backendBlob = await downloadSavedTechnicalReportPdf(reportWithArtifact, pdfBlob)

        return {
          report: reportWithArtifact,
          blob: backendBlob,
          filename: reportWithArtifact?.artifacts?.pdf?.filename || filename,
        }
      }

      if (!upload?.ok) {
        return {
          report: savedReport,
          blob: pdfBlob,
          filename,
        }
      }
    }

    if (res?.error_type === 'need_payment') {
      return false
    }

    return false
  }

  const handleDownloadPdf = async () => {
    if (selectedLands.length > MAX_TECHNICAL_REPORT_LANDS) {
      openAlert(
        'danger',
        t('reports:technical.errors.title'),
        t('reports:technical.errors.max_lands', {max: MAX_TECHNICAL_REPORT_LANDS})
      )
      return
    }

    if (!canDownload || isDownloading || generationStartedRef.current) {
      openAlert('danger', t('reports:technical.errors.title'), t('reports:technical.errors.incomplete'))
      return
    }

    generationStartedRef.current = true
    setIsDownloading(true)
    let savedReport = null

    const selectedContent = CONTENT_OPTIONS
      .filter((option) => content[option])
      .map((option) => t(`reports:technical.content.${option}`))
    const selectedImages = activeImageTypes.length > 0
      ? selectedLands.map((land) => {
        const landId = getLandId(land)
        const landSelections = getLandImageSelections(imageSelections, landId)

        return {
          landName: getFieldLandDisplayName(land),
          images: activeImageTypes.map((type) => ({
            label: t(`reports:technical.images.${type}`),
            count: landSelections[type].length,
            dates: landSelections[type].map(formatInputDate),
          })),
        }
      })
      : []

    const reportDraft = {
      id: reportId,
      createdAt: initialReport?.createdAt,
      name: reportName.trim(),
      dateRange,
      selected,
      content,
      imageEnhancements,
      imageSelections: persistedImageSelections,
      product,
      notes,
    }
    const processingReport = onSaveReport?.({
      ...reportDraft,
      status: 'in_progress',
    })

    closeEditor()
    await new Promise((resolve) => window.setTimeout(resolve, 0))

    try {
      const visualSections = await buildTechnicalReportVisualSections({
        currentUser,
        content,
        imageEnhancements,
        imageSelections: persistedImageSelections,
        product,
        labels: {
          class: t('ambient:legend.class'),
          content: CONTENT_OPTIONS.reduce((accumulator, option) => {
            accumulator[option] = t(`reports:technical.content.${option}`)
            return accumulator
          }, {}),
          images: IMAGE_TYPES.reduce((accumulator, type) => {
            accumulator[type] = t(`reports:technical.images.${type}`)
            return accumulator
          }, {}),
          enhanced: t('images:legend.enhanced'),
          legend: t('images:legend.title'),
          soilMap: {
            coverage: t('images:soil_map.coverage'),
            dataset: t('images:soil_map.dataset'),
            memory: t('images:soil_map.memory'),
            noCoverage: t('images:soil_map.no_coverage'),
            scale: t('images:soil_map.scale'),
            source: t('images:soil_map.source'),
            sourceUnavailable: t('images:soil_map.source_unavailable'),
            title: t('images:legend.title'),
            unknownUnit: t('images:soil_map.unknown_unit'),
          },
          unavailable: t('reports:technical.preview.unavailable', {defaultValue: 'No disponible'}),
        },
        selectedLands,
      })

      const downloaded = await downloadTechnicalReportPdf({
        content: selectedContent,
        dateRange: {
          from: formatInputDate(dateRange.from),
          to: formatInputDate(dateRange.to),
        },
        imageCounts: selectedImages,
        labels: {
          content: t('reports:technical.preview.content'),
          eyebrow: t('reports:technical.preview.eyebrow'),
          fields: t('reports:technical.preview.fields'),
          generatedAt: t('reports:technical.preview.generated_at'),
          generatedAtValue: format(new Date(), 'dd/MM/yyyy'),
          images: t('reports:technical.form.images.title'),
          lands: t('reports:technical.preview.lands'),
          noScope: t('reports:technical.preview.no_scope'),
          observations: t('reports:technical.preview.observations'),
          period: t('reports:technical.preview.period'),
          scope: t('reports:technical.preview.scope'),
          surface: t('reports:technical.preview.surface'),
          source: t('reports:technical.preview.source'),
        },
        notes,
        reportName: reportName.trim(),
        selectedFields: selectedFields.map((field) => ({
          ...field,
          lands: field.lands.map((land) => ({
            ...land,
            ha: formatHectares(getLandHa(land)),
          })),
        })),
        selectedLands,
        totalHa: formatHectares(totalHa),
        sourceLabel: editorSourceLabel,
        visualSections,
        beforeDownload: async ({blob, filename}) => {
          const savedDownload = await saveReportBeforeDownload(reportId, blob, filename)

          if (!savedDownload) {
            return false
          }

          savedReport = savedDownload.report
          return {
            blob: savedDownload.blob,
            filename: savedDownload.filename,
          }
        },
      })

      if (!downloaded) {
        onSaveReport?.({
          ...reportDraft,
          createdAt: processingReport?.createdAt || reportDraft.createdAt,
          status: 'error',
        })
        return
      }

      onSaveReport?.(savedReport || {
        ...reportDraft,
        createdAt: processingReport?.createdAt || reportDraft.createdAt,
        status: 'completed',
      })
      openAlert(
        'success',
        t('reports:technical.save.success.title'),
        t('reports:technical.save.success.message')
      )
    } catch (error) {
      console.error(error)
      onSaveReport?.({
        ...reportDraft,
        createdAt: processingReport?.createdAt || reportDraft.createdAt,
        status: 'error',
      })
      openAlert(
        'danger',
        t('reports:technical.errors.title'),
        t('reports:technical.errors.pdf_generation', {
          defaultValue: 'No pudimos generar el PDF con las capas seleccionadas.',
        })
      )
    }
  }

  return (
    <TechnicalReportContent
      historicalHdAllowed={historicalHdAllowed}
      activeImageTypes={activeImageTypes}
      canDownload={canDownload}
      content={content}
      dateRange={dateRange}
      fields={fields}
      hasRequiredImageDates={hasRequiredImageDates}
      imageEnhancements={imageEnhancements}
      imageSelections={imageSelections}
      imageThumbnailCache={imageThumbnailCache}
      isDownloading={isDownloading}
      mobile={mobile}
      notes={notes}
      onAddImageDate={addImageDate}
      onApplyImageSelectionsToAllLands={applyImageSelectionsToAllLands}
      onBack={closeEditor}
      onDownload={handleDownloadPdf}
      onCopyImageDates={copyImageDates}
      onRemoveImageDate={removeImageDate}
      onSetImageDates={setImageDates}
      onToggleImageEnhancement={toggleImageEnhancement}
      imageDateState={imageDateState}
      product={product}
      reportName={reportName}
      selected={selected}
      selectedFields={selectedFields}
      selectedLands={selectedLands}
      setDateRange={setDateRange}
      setNotes={setNotes}
      setProduct={selectProduct}
      setReportName={setReportName}
      sourceLabel={editorSourceLabel}
      toggleContent={toggleContent}
      toggleField={toggleField}
      toggleLand={toggleLand}
      totalHa={totalHa}
    />
  )
}

const TechnicalReportEditorContainer = ({onSaveReport, reports = []}) => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const {id} = useParams()
  const initialReport = id ? reports.find((report) => report.id === id) : null

  useEffect(() => {
    if (id && !initialReport) {
      navigate('/gis/reports/technical')
    }
  }, [id, initialReport, navigate])

  if (id && !initialReport) {
    return null
  }

  const editor = (
    <TechnicalReportEditor
      key={initialReport?.id || 'new'}
      initialReport={initialReport}
      onClose={() => navigate('/gis/reports/technical')}
      onSaveReport={onSaveReport}
    />
  )

  if (isMobile) {
    return (
      <div className='reports-mobile-shell displayable'>
        <div className='card shadow-sm right-card-data reports-mobile-card reports-mobile-card--editor technical-report-card'>
          <TechnicalReportEditor
            key={initialReport?.id || 'new'}
            initialReport={initialReport}
            mobile
            onClose={() => navigate('/gis/reports/technical')}
            onSaveReport={onSaveReport}
          />
        </div>
      </div>
    )
  }

  return (
    <Modal open={true} setOpen={() => navigate('/gis/reports/technical')} focus={false}>
      <div className='modal-dialog modal-dialog-centered technical-report-modal'>
        <div className='modal-content technical-report-card'>
          {editor}
        </div>
      </div>
    </Modal>
  )
}

const TechnicalReportContent = ({
  historicalHdAllowed = false,
  activeImageTypes,
  canDownload,
  content,
  dateRange,
  fields,
  hasRequiredImageDates,
  imageSelections,
  imageEnhancements,
  imageThumbnailCache,
  imageDateState,
  isDownloading,
  mobile,
  notes,
  onAddImageDate,
  onApplyImageSelectionsToAllLands,
  onBack,
  onCopyImageDates,
  onDownload,
  onRemoveImageDate,
  onSetImageDates,
  onToggleImageEnhancement,
  product,
  reportName,
  selected,
  selectedFields,
  selectedLands,
  setDateRange,
  setNotes,
  setProduct,
  setReportName,
  sourceLabel,
  toggleContent,
  toggleField,
  toggleLand,
  totalHa,
}) => {
  const {t} = useTranslation()
  const [search, setSearch] = useState('')
  const filteredFields = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean)

    if (words.length < 1) {
      return fields
    }

    return fields
      .map((field) => {
        const fieldName = (field.name || '').toLowerCase()
        const fieldMatches = words.some((word) => fieldName.includes(word))
        const lands = (field.lands || []).filter((land) => {
          const landName = (land.name || '').toLowerCase()
          return fieldMatches || words.some((word) => landName.includes(word))
        })

        return {...field, lands}
      })
      .filter((field) => (field.lands || []).length > 0)
  }, [fields, search])
  const clearImageDates = useMemo(
    () => sortDatesDescending(Array.from(new Set(
      Object.values(imageDateState.byLand || {}).flatMap((landState) => landState.clearDates || [])
    ))),
    [imageDateState.byLand]
  )
  const cloudyImageDates = useMemo(
    () => sortDatesDescending(Array.from(new Set(
      Object.values(imageDateState.byLand || {}).flatMap((landState) => landState.cloudyDates || [])
    ))),
    [imageDateState.byLand]
  )
  const dateSelectionDisabled = selectedLands.length < 1 || !['ready', 'outside_archive'].includes(imageDateState.status)
  const [activeStep, setActiveStep] = useState('identity')
  const productContentOptions = getTechnicalReportImageTypes(product).includes('ndre')
    ? CONTENT_OPTIONS
    : CONTENT_OPTIONS.filter((option) => option !== 'ndre_images')
  const availableContentOptions = productContentOptions.filter((option) => (
    option !== 'historical_ambient' || !isPlanetHdProduct(product) || historicalHdAllowed
  ))
  const hasContentSelected = availableContentOptions.some((option) => Boolean(content[option]))
  const selectedLandsExceedsLimit = selectedLands.length > MAX_TECHNICAL_REPORT_LANDS
  const selectedLandsReachedLimit = selectedLands.length === MAX_TECHNICAL_REPORT_LANDS
  const scopeLimitClassName = [
    'technical-report-scope-limit',
    selectedLandsExceedsLimit ? 'is-error' : '',
    selectedLandsReachedLimit ? 'is-full' : '',
  ].filter(Boolean).join(' ')
  const wizardSteps = useMemo(() => {
    const steps = [
      {id: 'identity', label: t('reports:technical.form.identity.title')},
      {id: 'scope', label: t('reports:ndvi.create.inputs.lands.title')},
      {id: 'dates', label: t('reports:technical.form.dates.title')},
      {id: 'content', label: t('reports:technical.form.content.title')},
    ]

    if (activeImageTypes.length > 0) {
      steps.push({id: 'images', label: t('reports:technical.form.images.title')})
    }

    steps.push(
      {id: 'observations', label: t('reports:technical.form.observations.title')},
      {id: 'preview', label: t('reports:technical.wizard.preview')}
    )

    return steps
  }, [activeImageTypes.length, t])
  const activeStepIndex = Math.max(
    wizardSteps.findIndex((step) => step.id === activeStep),
    0
  )
  const currentStep = wizardSteps[activeStepIndex] || wizardSteps[0]
  const isLastStep = activeStepIndex === wizardSteps.length - 1
  const getStepIsComplete = (stepId) => {
    switch (stepId) {
      case 'identity':
        return reportName.trim().length > 0
      case 'scope':
        return selectedLands.length > 0 && !selectedLandsExceedsLimit
      case 'dates':
        return Boolean(dateRange.from && dateRange.to) && imageDateState.status === 'ready'
      case 'content':
        return hasContentSelected
      case 'images':
        return hasRequiredImageDates
      case 'observations':
        return true
      case 'preview':
        return canDownload
      default:
        return false
    }
  }
  const canGoNext = getStepIsComplete(currentStep.id)
  const canOpenStep = (stepIndex) =>
    stepIndex <= activeStepIndex ||
    wizardSteps.slice(0, stepIndex).every((step) => getStepIsComplete(step.id))

  useEffect(() => {
    if (wizardSteps.some((step) => step.id === activeStep)) {
      return
    }

    const orderIndex = Math.max(TECHNICAL_REPORT_WIZARD_ORDER.indexOf(activeStep), 0)
    const fallbackStep = wizardSteps[Math.min(orderIndex, wizardSteps.length - 1)] || wizardSteps[0]

    if (fallbackStep) {
      setActiveStep(fallbackStep.id)
    }
  }, [activeStep, wizardSteps])
  const goToPreviousStep = () => {
    const previousStep = wizardSteps[activeStepIndex - 1]

    if (previousStep) {
      setActiveStep(previousStep.id)
    }
  }
  const goToNextStep = () => {
    const nextStep = wizardSteps[activeStepIndex + 1]

    if (nextStep && canGoNext) {
      setActiveStep(nextStep.id)
    }
  }

  return (
    <>
      {mobile ? (
        <div className='reports-mobile-card__header technical-report-card__header'>
          <button
            type='button'
            className='btn btn-sm btn-light reports-mobile-card__back'
            onClick={onBack}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon svg-icon-2' />
            <span>{t('common:navigation.back_reports')}</span>
          </button>
          <div className='reports-mobile-card__eyebrow'>{t('reports:selector.report_label')}</div>
          <h2 className='reports-mobile-card__title'>{t('reports:technical.title')}</h2>
        </div>
      ) : (
        <div className='modal-header technical-report-card__modal-header'>
          <h5 className='modal-title'>{t('reports:technical.title')}</h5>
        </div>
      )}
      <div className={`${mobile ? 'reports-mobile-card__body' : 'modal-body reports-desktop-form'} technical-report-card__body`}>
        <div className='technical-report-wizard' aria-label={t('reports:technical.wizard.title')}>
          <div className='technical-report-wizard__meta'>
            {t('reports:technical.wizard.progress', {
              current: activeStepIndex + 1,
              total: wizardSteps.length,
            })}
          </div>
          <div className='technical-report-wizard__track technical-report-wizard__track--current-only'>
            {[currentStep].map((step) => {
              const stepIndex = activeStepIndex
              const isActive = step.id === currentStep.id
              const isComplete = getStepIsComplete(step.id)
              const isAvailable = canOpenStep(stepIndex)

              return (
                <button
                  type='button'
                  className={`technical-report-wizard__step ${isActive ? 'is-active' : ''} ${
                    isComplete ? 'is-complete' : ''
                  }`.trim()}
                  disabled={!isAvailable}
                  key={step.id}
                  onClick={() => {}}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <span>{stepIndex + 1}</span>
                  <strong>{step.label}</strong>
                </button>
              )
            })}
          </div>
        </div>

        {currentStep.id === 'identity' && (
          <section className='technical-report-form-section'>
            <h3>{t('reports:technical.form.identity.title')}</h3>
            <input
              className='form-control form-control-solid'
              type='text'
              value={reportName}
              maxLength={64}
              onChange={(event) => setReportName(event.target.value)}
              placeholder={t('reports:technical.form.name.placeholder')}
            />
            {PLANET_HD_V1_ENABLED && (
              <div className='mt-5'>
                <strong className='d-block mb-2'>
                  {t('reports:technical.form.product.title')}
                </strong>
                <div className='d-flex flex-wrap gap-2' role='group'>
                  {[
                    REMOTE_SENSING_PRODUCTS.SENTINEL,
                    REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD,
                  ].map((option) => (
                    <button
                      type='button'
                      className={`btn btn-sm ${product === option ? 'btn-primary' : 'btn-light-primary'}`}
                      key={option}
                      onClick={() => setProduct(option)}
                    >
                      {option === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
                        ? t('images:product.planet_hd')
                        : t('images:product.sentinel')}
                    </button>
                  ))}
                </div>
                <small className='text-muted d-block mt-2'>
                  {product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
                    ? t('reports:technical.form.product.hd_help')
                    : t('reports:technical.form.product.sentinel_help')}
                </small>
              </div>
            )}
          </section>
        )}

        {currentStep.id === 'scope' && (
          <section className='technical-report-form-section technical-report-form-section--scope'>
            <div className='technical-report-section-title'>
              <h3>{t('reports:ndvi.create.inputs.lands.title')}</h3>
              <span>{formatHectares(totalHa)}</span>
            </div>
            <div className={scopeLimitClassName} role='status' aria-live='polite'>
              <span>
                {t('reports:technical.form.scope.selected_count', {
                  max: MAX_TECHNICAL_REPORT_LANDS,
                  selected: selectedLands.length,
                })}
              </span>
              <strong>
                {t('reports:technical.form.scope.max_lands_notice', {
                  max: MAX_TECHNICAL_REPORT_LANDS,
                })}
              </strong>
            </div>
            <input
              type='text'
              className='form-control form-control-solid technical-report-scope-search'
              placeholder='Buscar'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <div
              className={`${mobile ? 'reports-mobile-field-groups' : 'accordion'} technical-report-scope-list`}
              id='technical_report_scope_accordion'
            >
              {filteredFields.map((field) => (
                <FieldSelector
                  field={field}
                  key={field.field_id}
                  mobile={mobile}
                  selected={selected}
                  toggleField={toggleField}
                  toggleLand={toggleLand}
                />
              ))}
              {filteredFields.length < 1 && (
                <p className='technical-report-empty'>{t('reports:technical.form.scope.empty')}</p>
              )}
            </div>
          </section>
        )}

        {currentStep.id === 'dates' && (
          <section className='technical-report-form-section'>
            <h3>{t('reports:technical.form.dates.title')}</h3>
            {selectedLands.length < 1 && (
              <small className='technical-report-image-selector__hint'>
                {t('reports:technical.form.images.select_land_first')}
              </small>
            )}
            {selectedLands.length > 0 && imageDateState.status === 'loading' && (
              <small className='technical-report-image-selector__hint technical-report-image-selector__hint--loading'>
                <span>{t('reports:technical.form.images.loading_dates')}</span>
                <span className='spinner-border spinner-border-sm' />
              </small>
            )}
            {selectedLands.length > 0 && imageDateState.status === 'access_not_ready' && (
              <small className='technical-report-image-selector__hint text-danger'>
                {t('reports:technical.form.product.hd_access_not_ready')}
              </small>
            )}
            {selectedLands.length > 0 && ['error', 'feature_disabled'].includes(imageDateState.status) && (
              <small className='technical-report-image-selector__hint text-danger'>
                {t('reports:technical.form.product.hd_load_error')}
              </small>
            )}
            {selectedLands.length > 0 && imageDateState.status === 'outside_archive' && (
              <small className='technical-report-image-selector__hint text-danger'>
                {t('reports:technical.form.product.hd_archive_limit')}
              </small>
            )}
            <div className='technical-report-date-grid'>
              <ReportDateSelector
                clearDates={clearImageDates}
                hdOnly={isPlanetHdProduct(product)}
                cloudyDates={cloudyImageDates}
                disabled={dateSelectionDisabled}
                label={t('reports:technical.form.dates.from')}
                value={dateRange.from}
                onChange={(value) => setDateRange((current) => ({...current, from: value}))}
              />
              <ReportDateSelector
                clearDates={clearImageDates}
                hdOnly={isPlanetHdProduct(product)}
                cloudyDates={cloudyImageDates}
                disabled={dateSelectionDisabled}
                label={t('reports:technical.form.dates.to')}
                value={dateRange.to}
                onChange={(value) => setDateRange((current) => ({...current, to: value}))}
              />
            </div>
          </section>
        )}

        {currentStep.id === 'content' && (
          <section className='technical-report-form-section'>
            <h3>{t('reports:technical.form.content.title')}</h3>
            <div className='technical-report-content-grid'>
              {availableContentOptions.map((option) => (
                <label className='technical-report-content-option' key={option}>
                  <span>{t(`reports:technical.content.${option}`)}</span>
                  <input
                    type='checkbox'
                    checked={Boolean(content[option])}
                    onChange={() => toggleContent(option)}
                  />
                </label>
              ))}
            </div>
          </section>
        )}

        {currentStep.id === 'images' && activeImageTypes.length > 0 && (
          <section className='technical-report-form-section'>
            <h3>{t('reports:technical.form.images.title')}</h3>
            <div className='technical-report-image-counts'>
              {selectedLands.length < 1 && (
                <small className='technical-report-image-selector__hint'>
                  {t('reports:technical.form.images.select_land_first')}
                </small>
              )}
              {selectedLands.map((land, index) => (
                <TechnicalReportLandImageCard
                  activeImageTypes={activeImageTypes}
                  dateRange={dateRange}
                  imageEnhancements={imageEnhancements}
                  imageDateState={imageDateState}
                  imageSelections={imageSelections}
                  initialOpen={index === 0}
                  key={getLandId(land)}
                  land={land}
                  onAddImageDate={onAddImageDate}
                  onApplySelectionsToAll={onApplyImageSelectionsToAllLands}
                  onCopyImageDates={onCopyImageDates}
                  onToggleImageEnhancement={onToggleImageEnhancement}
                  onRemoveImageDate={onRemoveImageDate}
                  onSetImageDates={onSetImageDates}
                  product={product}
                  selectedLandIds={selectedLands.map(getLandId).filter(Boolean)}
                  thumbnailCache={imageThumbnailCache}
                />
              ))}
            </div>
          </section>
        )}

        {currentStep.id === 'observations' && (
          <section className='technical-report-form-section'>
            <h3>{t('reports:technical.form.observations.title')}</h3>
            <textarea
              className='form-control form-control-solid'
              rows='3'
              value={notes.observations}
              onChange={(event) => setNotes((current) => ({...current, observations: event.target.value}))}
            />
          </section>
        )}

        {currentStep.id === 'preview' && (
          <TechnicalReportPreview
            screen
            content={content}
            dateRange={dateRange}
            imageSelections={imageSelections}
            notes={notes}
            product={product}
            reportName={reportName}
            selectedFields={selectedFields}
            selectedLands={selectedLands}
            sourceLabel={sourceLabel}
            totalHa={totalHa}
          />
        )}
      </div>
      <div className={`${mobile ? 'reports-mobile-form__footer' : 'modal-footer'} technical-report-card__footer`}>
        <button type='button' className='btn btn-light' onClick={onBack}>
          {t('common:actions.cancel')}
        </button>
        {activeStepIndex > 0 && (
          <button type='button' className='btn btn-light' onClick={goToPreviousStep}>
            {t('reports:technical.wizard.previous')}
          </button>
        )}
        <span className='technical-report-card__footer-spacer' />
        {isLastStep ? (
          <button
            type='button'
            className='btn btn-primary technical-report-card__download'
            disabled={!canDownload || isDownloading}
            onClick={onDownload}
          >
            {isDownloading
              ? t('reports:technical.buttons.downloading_pdf', {defaultValue: 'Generando PDF...'})
              : t('reports:technical.wizard.create')}
          </button>
        ) : (
          <button
            type='button'
            className='btn btn-primary technical-report-card__next'
            disabled={!canGoNext}
            onClick={goToNextStep}
          >
            {t('reports:technical.wizard.next')}
          </button>
        )}
      </div>
    </>
  )
}

const TechnicalReport = () => {
  const isMobile = useIsMobile()
  const {currentUser} = useAuth()
  const {errorHandler} = useErrorHandler()
  const errorHandlerRef = useRef(errorHandler)
  const isCreateMobile = useMatch('/gis/reports/technical/new')
  const isEditMobile = useMatch('/gis/reports/technical/edit/:id')
  const isViewMobile = useMatch('/gis/reports/technical/view/:id')
  const reportsStorageKey = useMemo(() => buildTechnicalReportCacheKey({
    tenantDomain: getTechnicalReportTenantDomain(),
    currentUser,
  }), [currentUser])
  const [reportsState, setReportsState] = useState(() => createTechnicalReportCacheState(
    reportsStorageKey,
    readStoredTechnicalReports(reportsStorageKey)
  ))
  const reports = getVisibleTechnicalReports(reportsState, reportsStorageKey)

  useEffect(() => {
    errorHandlerRef.current = errorHandler
  }, [errorHandler])

  useEffect(() => {
    let active = true
    const cachedReports = readStoredTechnicalReports(reportsStorageKey)

    removeLegacyTechnicalReportCache()
    setReportsState(createTechnicalReportCacheState(reportsStorageKey, cachedReports))

    const loadReports = async () => {
      let res
      try {
        res = await errorHandlerRef.current(
          ReportService.listTechnicalReports(0, TECHNICAL_REPORT_PAGE_SIZE),
          [400, 404, 422, 500]
        )
      } catch (error) {
        return
      }

      if (!active || !res?.ok) {
        return
      }

      const next = (res.data.technical_reports || [])
        .map(normalizeStoredTechnicalReport)
        .filter(Boolean)
        .sort((left, right) => new Date(right.updatedAt) - new Date(left.updatedAt))

      setReportsState(createTechnicalReportCacheState(reportsStorageKey, next))
      writeStoredTechnicalReports(reportsStorageKey, next)
    }

    loadReports()

    return () => {
      active = false
    }
  }, [reportsStorageKey])

  const setReports = (updater) => {
    setReportsState((current) => {
      const currentReports = getVisibleTechnicalReports(current, reportsStorageKey)
      const next = typeof updater === 'function' ? updater(currentReports) : updater

      writeStoredTechnicalReports(reportsStorageKey, next)
      return createTechnicalReportCacheState(reportsStorageKey, next)
    })
  }

  const saveTechnicalReport = (draft) => {
    const report = buildTechnicalReportRecord(draft)

    setReports((current) => {
      const next = [
        report,
        ...current.filter((item) => item.id !== report.id),
      ].sort((left, right) => new Date(right.updatedAt) - new Date(left.updatedAt))

      return next
    })

    return report
  }

  const deleteTechnicalReport = async (reportId) => {
    setReports((current) => {
      const next = current.filter((report) => report.id !== reportId)

      return next
    })

    await errorHandler(ReportService.deleteTechnicalReport(reportId), [400, 404, 422, 500])
  }

  const nestedRoutes = (
    <Routes>
      <Route
        path='new'
        element={<TechnicalReportEditorContainer onSaveReport={saveTechnicalReport} reports={reports} />}
      />
      <Route
        path='edit/:id'
        element={<TechnicalReportEditorContainer onSaveReport={saveTechnicalReport} reports={reports} />}
      />
      <Route
        path='view/:id'
        element={(
          <TechnicalReportDetailContainer
            onDeleteReport={deleteTechnicalReport}
            reports={reports}
          />
        )}
      />
    </Routes>
  )

  if (isMobile) {
    if (isCreateMobile || isEditMobile || isViewMobile) {
      return nestedRoutes
    }

    return (
      <>
        <TechnicalReportList
          mobile
          onDeleteReport={deleteTechnicalReport}
          reports={reports}
        />
        {nestedRoutes}
      </>
    )
  }

  return (
    <>
      <div className='d-flex flex-column flex-row-auto field-view-flex-container'>
        <div className='d-flex flex-column-auto'>
          <TechnicalReportList
            onDeleteReport={deleteTechnicalReport}
            reports={reports}
          />
        </div>
      </div>
      {nestedRoutes}
    </>
  )
}

export default TechnicalReport
